import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from "react";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Checkbox,
  Typography,
  Alert,
  Row,
  Col,
  Tag,
  Switch,
  Space,
  Modal,
  Tabs,
  Tooltip,
  Collapse,
} from "antd";
import { PlusOutlined, DeleteOutlined, EyeOutlined, ArrowRightOutlined, SettingOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTender } from "../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import { formatCurrency } from "../../utils/calculations";
import MainLayout from "../layout/MainLayout";
import {
  RoadType,
  CutFillSection,
  CappingLayer,
  CappingType,
  BaseCourseType,
  BaseAttachmentCode,
  BinderCourseType,
  SurfaceCourseGroup,
  CarriagewayFootway,
  FootpathsConstructionConfig,
} from "../../types/tender.types";
import FootpathsConfigModal, {
  DEFAULT_FOOTPATHS_CONFIG,
  calculateFootpathsAverageBuildUp,
} from "./FootpathsConfigModal";
import {
  TARMAC_SURFACE_COURSE_TYPES,
  BLOCK_PAVED_SURFACE_COURSE_TYPES,
  CAPPING_LAYER_THICKNESSES,
  CAPPING_TYPES,
  BASE_COURSE_OPTIONS,
  BASE_ATTACHMENT_OPTIONS,
  BASE_ATTACHMENT_NONE,
  BINDER_COURSE_OPTIONS,
  SURFACE_COURSE_GROUPS,
  SURFACE_COURSE_OPTIONS_BY_GROUP,
  DEFAULT_ROAD_CONSTRUCTION,
  COLORS,
  formatUnit,
} from "../../utils/constants";
import {
  calculateTotalConstructionThickness,
  calculateRoadSummary,
  groupCutFillSectionsByRoad,
  computeLiveCutFillGroups,
  computeLiveFootpathsGroups,
  dedupeSectionsByChainage,
} from "../../utils/roadCalculations";
import { findCappingLayersEvaluateCode, findCappingMaterialEvaluateCode } from "../../utils/roadsEvaluateCodesMapping";
import CarriagewayFootwaySection from "./CarriagewayFootwaySection";
import FootwaysTakeoffSection from "./FootwaysTakeoffSection";
import KerbsEdgingsSection from "./KerbsEdgingsSection";
import CarriagewaySummary, { CARRIAGEWAY_SUMMARY_SECTION_KEYS } from "./roads-sewers/CarriagewaySummary";
import { ColumnConfigModal, ConfigField } from "../common/ColumnConfigModal";
import NumberInput from "../common/NumberInput";
import { useTableRenderDeferral } from "../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../common/TableLoadingPlaceholder";
import ExpandCollapseAllButton from "../common/ExpandCollapseAllButton";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { roadRates } from "../../mock";
import {
  exportRoadsToExcel,
  importRoadsFromExcel,
  logCarriagewayTakeoffSheet,
  logFootpathsConfigData,
  logCarriagewaySheetSurvey,
  logCutFillAndFootpathsData,
  parseCarriagewayTakeoffRoadTypes,
  parseFootpathsConfig,
  parseCarriagewayAndFootwaysData,
  parseCutFillSections,
  parseStripValue,
  parseKerbRaceWidth,
  parseRoadConstructionTypeSelections,
  parseCarriagewaySummaryManualQtys,
  parseCappingLayersFromExcel,
  generateRoadsTemplate,
  validateRoadsData,
  RoadsExcelData,
} from "../../utils/roadsExcelUtils";
import {
  ExcelImportExportButtons,
  ExcelImportModal,
} from "../common/ExcelImportExport";
import { roundNearest } from '../../utils/roundingUtils';

// Legacy fixed capping sections, shown until the user adds their own via the Capping Layer tab.
const DEFAULT_CAPPING_THICKNESSES = [150, 350, 600];

const DEFAULT_ROAD_TYPES: RoadType[] = [
  { id: 'tarmac-1', name: 'Tarmac Spec 1', surface: 'Tarmac Road', membrane: false, surfaceCourse: { type: 'HRA 55/10', thickness: 0 }, binderCourse: { thickness: 0 }, baseCourse: { thickness: 0 }, subBase: { type: 'Type 1', thickness: 0 }, capping: 0, eoPreCoat: false, eoPSV65: false, sandPave: 0, totalConstructionThickness: 0 },
  { id: 'tarmac-2', name: 'Tarmac Spec 2', surface: 'Tarmac Road', membrane: false, surfaceCourse: { type: 'HRA 55/10', thickness: 0 }, binderCourse: { thickness: 0 }, baseCourse: { thickness: 0 }, subBase: { type: 'Type 1', thickness: 0 }, capping: 0, eoPreCoat: false, eoPSV65: false, sandPave: 0, totalConstructionThickness: 0 },
  { id: 'tarmac-3', name: 'Tarmac Spec 3', surface: 'Tarmac Road', membrane: false, surfaceCourse: { type: 'HRA 55/10', thickness: 0 }, binderCourse: { thickness: 0 }, baseCourse: { thickness: 0 }, subBase: { type: 'Type 1', thickness: 0 }, capping: 0, eoPreCoat: false, eoPSV65: false, sandPave: 0, totalConstructionThickness: 0 },
  { id: 'tarmac-4', name: 'Tarmac Spec 4', surface: 'Tarmac Road', membrane: false, surfaceCourse: { type: 'HRA 55/10', thickness: 0 }, binderCourse: { thickness: 0 }, baseCourse: { thickness: 0 }, subBase: { type: 'Type 1', thickness: 0 }, capping: 0, eoPreCoat: false, eoPSV65: false, sandPave: 0, totalConstructionThickness: 0 },
  { id: 'block-1', name: 'Block Paved Spec 1', surface: 'Block Paved Road', membrane: false, surfaceCourse: { type: '200x100x60mm Keybloc Paving', thickness: 0 }, binderCourse: { thickness: 0 }, baseCourse: { thickness: 0 }, subBase: { type: 'Type 1', thickness: 0 }, capping: 0, eoPreCoat: false, eoPSV65: false, sandPave: 0, totalConstructionThickness: 0 },
  { id: 'block-2', name: 'Block Paved Spec 2', surface: 'Block Paved Road', membrane: false, surfaceCourse: { type: '200x100x60mm Keybloc Paving', thickness: 0 }, binderCourse: { thickness: 0 }, baseCourse: { thickness: 0 }, subBase: { type: 'Type 1', thickness: 0 }, capping: 0, eoPreCoat: false, eoPSV65: false, sandPave: 0, totalConstructionThickness: 0 },
  { id: 'block-3', name: 'Block Paved Spec 3', surface: 'Block Paved Road', membrane: false, surfaceCourse: { type: '200x100x60mm Keybloc Paving', thickness: 0 }, binderCourse: { thickness: 0 }, baseCourse: { thickness: 0 }, subBase: { type: 'Type 1', thickness: 0 }, capping: 0, eoPreCoat: false, eoPSV65: false, sandPave: 0, totalConstructionThickness: 0 },
  { id: 'block-4', name: 'Block Paved Spec 4', surface: 'Block Paved Road', membrane: false, surfaceCourse: { type: '200x100x60mm Keybloc Paving', thickness: 0 }, binderCourse: { thickness: 0 }, baseCourse: { thickness: 0 }, subBase: { type: 'Type 1', thickness: 0 }, capping: 0, eoPreCoat: false, eoPSV65: false, sandPave: 0, totalConstructionThickness: 0 },
];

// Defined at module level so React sees a stable component type across renders.
const CfColTitle: React.FC<{ title: string; unit?: string; onInfoClick?: () => void }> = ({
  title,
  unit,
  onInfoClick,
}) =>
  onInfoClick ? (
    <div className="text-center">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {title}
        </span>
        <InfoCircleOutlined
          style={{ color: COLORS.primary, cursor: "pointer", flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onInfoClick();
          }}
        />
      </div>
      {unit && <div className="cf-header-unit">{unit}</div>}
    </div>
  ) : (
    <div className="text-center">
      <Tooltip title={title}>
        <div className="cf-header-title">{title}</div>
      </Tooltip>
      {unit && <div className="cf-header-unit">{unit}</div>}
    </div>
  );

const KERB_RACE_WIDTH_OPTIONS = [0.5, 0.6, 0.7, 0.725, 0.75];
const CUT_FILL_PAGE_SIZE = 25;

// Carriageway Cut & Fill and Footpaths formulas (per R&S-Takeoffsheet.xlsx, "Carriageway
// Take off" sheet) live in `computeCutFillFields`/`computeFootpathsFields`
// (`src/utils/roadCalculations.ts`) — shared with `RoadsBillSummary.tsx`'s Carriageway
// Summary tab so both compute the same live values instead of drifting out of sync.

const RoadsTakeoff: React.FC = () => {
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const { state, updateField, saveTender } = useTender();
  useAutoSave();
  const navigate = useNavigate();
  const showDevMode = useDevMode();

  // Item library for dev-mode rate lookups by evaluate code — only fetched
  // when the dev-only Evaluate Code column (Capping Layers) is actually shown.
  const { data: itemLibraryData } = useItemLibrary(state.region || "", undefined, {
    enabled: showDevMode,
  });
  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item.nettRate || item.baseRate || 0);
    });
    return map;
  }, [itemLibraryData]);
  const getLibRate = (code?: string): number | undefined => {
    if (!code) return undefined;
    return resourceCodeMap.get(code.trim().toUpperCase());
  };

  const handleSaveAndContinue = () => {
    updateField("status", "In-progress");
    updateField("boqStatus", "In-progress");
    updateField("lastModified", new Date());
    saveTender();
    navigate("/roads-sewers/main-services-substation");
  };

  const [isViewRatesModalOpen, setIsViewRatesModalOpen] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("carriageway-footways");
  // Defers this step's per-section tables' mount by one paint cycle so a
  // tender with a lot of roads/sections doesn't make the tab look frozen
  // while the table's render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Re-keyed on `activeTab` since
  // switching tabs here unmounts/mounts a brand new Table, not just a
  // dataSource update - so the loader should reappear on every tab switch,
  // not just the initial mount. Purely a perceived-lag fix: once it flips
  // false the table(s) render exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab]);
  const [stickyOffset, setStickyOffset] = useState(310);

  // Shared row-delete confirmation — matches the DeleteConfirmationModal pattern
  // used by Groundworks tables, rather than the antd Popconfirm previously used here.
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<(() => void) | null>(null);
  const [pendingDeleteCount, setPendingDeleteCount] = useState(1);
  const requestDelete = (action: () => void, count: number = 1) => {
    setPendingDelete(() => action);
    setPendingDeleteCount(count);
    setDeleteModalVisible(true);
  };
  const confirmDelete = () => {
    pendingDelete?.();
    setDeleteModalVisible(false);
    setPendingDelete(null);
  };
  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setPendingDelete(null);
  };
  const [cutFillConfigOpen, setCutFillConfigOpen] = useState(false);
  const [kerbsEdgingsConfigOpen, setKerbsEdgingsConfigOpen] = useState(false);
  const [footpathsConfigOpen, setFootpathsConfigOpen] = useState(false);
  const [roadTypesConfigOpen, setRoadTypesConfigOpen] = useState(false);
  // Cut & Fill / Kerbs & Edgings / Road Types / Footpaths Configure modals write
  // straight to state on every field change (no local draft), so closing via the
  // X icon/mask click/Esc (as opposed to "Done") previously kept whatever had
  // already been typed. These snapshots — captured when each modal opens — let
  // that close path revert to the pre-open value; "Done" leaves the live edits as is.
  const cutFillConfigSnapshotRef = useRef<{ stripValueDefault: number; tarmacConstructionTypeId?: string; blockPavedConstructionTypeId?: string } | null>(null);
  const kerbsEdgingsConfigSnapshotRef = useRef<{ kerbRaceWidth?: number; totalBuildUpValue: number } | null>(null);
  const roadTypesConfigSnapshotRef = useRef<RoadType[] | null>(null);
  const footpathsConfigSnapshotRef = useRef<FootpathsConstructionConfig | null>(null);
  const [cappingActiveKeys, setCappingActiveKeys] = useState<string[]>([]);
  const cappingScrollOuterRef = useRef<HTMLDivElement>(null);
  const [cappingViewportWidth, setCappingViewportWidth] = useState(0);
  const [carriagewaySummaryExpandedKeys, setCarriagewaySummaryExpandedKeys] = useState<React.Key[]>(CARRIAGEWAY_SUMMARY_SECTION_KEYS);

  interface CappingFormulaRow { ref: string; value: number; unit: string }
  interface CappingFormulaInfo {
    label: string;
    formula: string;
    excelRef: string;
    rows: CappingFormulaRow[];
    total: number;
    unit: string;
  }
  const [cappingActiveFormula, setCappingActiveFormula] = useState<CappingFormulaInfo | null>(null);
  const [openCutFillRoads, setOpenCutFillRoads] = useState<string[]>([]);
  const [cutFillCurrentPage, setCutFillCurrentPage] = useState<Record<string, number>>({});
  const cutFillAddChainageBtnRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const cutFillAnchorRef = useRef<{ roadId: string; prevTop: number } | null>(null);
  useLayoutEffect(() => {
    const anchor = cutFillAnchorRef.current;
    if (!anchor) return;
    cutFillAnchorRef.current = null;
    const btnEl = cutFillAddChainageBtnRefs.current[anchor.roadId];
    if (!btnEl) return;
    const newTop = btnEl.getBoundingClientRect().top;
    const delta = newTop - anchor.prevTop;
    if (delta !== 0) {
      window.scrollBy(0, delta);
    }
  }, [cutFillCurrentPage]);
  const [openFootpathRoads, setOpenFootpathRoads] = useState<string[]>([]);
  const [cfPanelHeaderHeight, setCfPanelHeaderHeight] = useState(0);
  const [fpPanelHeaderHeight, setFpPanelHeaderHeight] = useState(0);

  interface ColCalcInfo {
    title: string;
    formula: string;
  }
  const [colCalcInfo, setColCalcInfo] = useState<ColCalcInfo | null>(null);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setOpenCutFillRoads([]);
    setOpenFootpathRoads([]);
    setCappingActiveKeys([]);
    setCarriagewaySummaryExpandedKeys(CARRIAGEWAY_SUMMARY_SECTION_KEYS);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const calculateOffset = () => {
      const baseOffset = 144;
      const stickyHeader = document.getElementsByClassName('roads-sticky-header')?.[0];
      const height = stickyHeader?.getBoundingClientRect?.()?.height || 0;
      setStickyOffset(baseOffset + height);
    };
    calculateOffset();
    const timer = setTimeout(calculateOffset, 100);
    return () => clearTimeout(timer);
  }, [activeTab]);

  // Table sticky header must sit below the sticky accordion panel header,
  // so its offset needs the panel header's own height added on top of stickyOffset.
  useEffect(() => {
    const measurePanelHeader = () => {
      const activeHeader = document.querySelector(
        '.cf-road-collapse .ant-collapse-item-active > .ant-collapse-header'
      );
      const height = activeHeader?.getBoundingClientRect?.()?.height || 0;
      setCfPanelHeaderHeight(height);
    };
    measurePanelHeader();
    const timer = setTimeout(measurePanelHeader, 100);
    return () => clearTimeout(timer);
  }, [openCutFillRoads, activeTab]);

  // Capping Layer has ONE shared horizontal-scroll wrapper (capping-scroll-outer)
  // spanning the header AND the entire accordion, so there's only ever one
  // scrollbar. Each accordion panel's own title bar ("150mm CAPPING" + gear/
  // delete buttons) is pinned in place with position:sticky; left:0 (same
  // mechanism as the Description/Quant/Unit/Action columns) so it doesn't move
  // as that shared wrapper scrolls — but sticky-left only pins the element's
  // left edge, it doesn't also constrain its width, so the title bar still
  // needs an explicit width matching the wrapper's own VISIBLE (unscrolled)
  // width rather than the full scrolled content's width, or its right-aligned
  // buttons would render far off to the right. This measures that width.
  useEffect(() => {
    if (activeTab !== 'capping-layer') return;
    const el = cappingScrollOuterRef.current;
    if (!el) return;
    const measure = () => setCappingViewportWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTab]);

  useEffect(() => {
    const measurePanelHeader = () => {
      const activeHeader = document.querySelector(
        '.fp-road-collapse .ant-collapse-item-active > .ant-collapse-header'
      );
      const height = activeHeader?.getBoundingClientRect?.()?.height || 0;
      setFpPanelHeaderHeight(height);
    };
    measurePanelHeader();
    const timer = setTimeout(measurePanelHeader, 100);
    return () => clearTimeout(timer);
  }, [openFootpathRoads, activeTab]);

  // Calculate rates for View Rates modal
  const calculateAllRoadRates = () => {
    // Calculate total area from Carriageway & Footways
    const totalCarriagewayArea = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) => sum + (cf.totalRoadArea || 0),
      0
    );
    const totalFootwaysArea = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) => sum + (cf.totalPath || 0),
      0
    );
    const totalArea = totalCarriagewayArea + totalFootwaysArea;

    // Calculate specific field totals
    const totalTarmacRoad = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) =>
        sum + (cf.tarmacSpec1 || 0) + (cf.tarmacSpec2 || 0) +
        (cf.tarmacSpec3 || 0) + (cf.tarmacSpec4 || 0),
      0
    );
    const totalBlockPaved = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) =>
        sum + (cf.blockPavedSpec1 || 0) + (cf.blockPavedSpec2 || 0) +
        (cf.blockPavedSpec3 || 0) + (cf.blockPavedSpec4 || 0),
      0
    );
    const totalTarmacPath = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) => sum + (cf.tarmacPath || 0),
      0
    );
    const totalCrossover = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) =>
        sum + (cf.tarmacCrossover || 0) + (cf.heavyDutyCrossover || 0) +
        (cf.blockPavedPathCrossover || 0),
      0
    );
    const totalVerge = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) => sum + (cf.verge || 0),
      0
    );
    const totalKerb = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) => sum + (cf.kerbRace || 0),
      0
    );
    const totalChannelKerb = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) => sum + (cf.keyChannel || 0),
      0
    );
    const totalEoRamps = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) => sum + (cf.eoRamps || 0),
      0
    );
    const totalEdgings = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) => sum + (cf.edgings150x50 || 0) + (cf.edgings200x50 || 0),
      0
    );
    const totalTactiles = (roadsData.carriagewayFootways.table || []).reduce(
      (sum, cf) => sum + (cf.tactilePaving || 0) + (cf.corduroyPaving || 0),
      0
    );

    // Calculate Cut & Fill volumes — from the live-derived road groups
    // (computeCutFillFields/computeFootpathsFields), not the raw stored table,
    // whose cutVolume/fillVolume/footpathsCutVolume/footpathsFillVolume fields
    // are only ever initialized to 0 and never written back as dependent
    // config (Kerb Race Width, Strip value, etc.) changes.
    const cutFillSectionsLive = cutFillCalculatedRoadGroups.flatMap((group) => group.sections);
    const footpathsSectionsLive = footpathsCutFillRoadGroups.flatMap((group) => group.sections);
    const totalCutVolumeCarriageways = cutFillSectionsLive.reduce(
      (sum, cf) => sum + (cf.cutVolume || 0),
      0
    );
    const totalCutVolumeFootpaths = footpathsSectionsLive.reduce(
      (sum, cf) => sum + (cf.footpathsCutVolume || 0),
      0
    );
    const totalFillVolumeCarriageways = cutFillSectionsLive.reduce(
      (sum, cf) => sum + (cf.fillVolume || 0),
      0
    );
    const totalFillVolumeFootpaths = footpathsSectionsLive.reduce(
      (sum, cf) => sum + (cf.footpathsFillVolume || 0),
      0
    );

    // Calculate capping area totals — from the same live-derived sections above.
    const totalCappingArea = cutFillSectionsLive.reduce((sum, cf) => sum + (cf.cappingArea || 0), 0);
    const totalRoadArea = totalCarriagewayArea + totalFootwaysArea;

    // Calculate capping ratios
    const cappingRatioTarmac =
      totalRoadArea > 0
        ? (totalCappingArea / totalRoadArea) * totalTarmacRoad
        : 0;
    const cappingRatioBlockPaved =
      totalRoadArea > 0
        ? (totalCappingArea / totalRoadArea) * totalBlockPaved
        : 0;

    // Site clearance calculations
    const siteClearanceCategory = roadRates.siteClearanceEarthworks.find(
      (category) => category.category === "SITE CLEARANCE & EARTHWORKS"
    );

    const siteStripItem = siteClearanceCategory?.items.find(
      (item) => item.description === "Site strip to remove vegetation"
    );

    const excavateItem = siteClearanceCategory?.items.find(
      (item) =>
        item.description ===
        "Excavate to reduce levels to take up 296mm top soil, deposit onsite"
    );

    const siteStripTotal = totalArea * (siteStripItem?.rate || 0);
    const stripValue = roadsData.cutFillSections.config.stripValueDefault || 0.15;
    const excavateAmount = siteStripTotal * stripValue;

    // Calculate gully totals from storm sewers
    const totalGullies = (state.stormSewers?.gullyConnections.table || []).length;

    return {
      // Site clearance & earthworks
      totalArea,
      totalCarriagewayArea,
      totalFootwaysArea,
      siteStripRate: siteStripItem?.rate || 0,
      siteStripUnit: siteStripItem?.unit || "m²",
      siteStripAmount: siteStripTotal,
      excavateRate: excavateItem?.rate || 0,
      excavateUnit: excavateItem?.unit || "m³",
      excavateAmount: excavateAmount,
      stripValue: stripValue,

      // Excavating & filling
      totalCutVolume: totalCutVolumeCarriageways + totalCutVolumeFootpaths,
      totalFillVolumeFootpaths,
      totalFillVolumeCarriageways,

      // Carriageway calculations
      totalTarmacRoad,
      totalBlockPaved,
      cappingRatioTarmac,
      cappingRatioBlockPaved,
      totalEoRamps,

      // Footpaths calculations
      totalTarmacPath,
      totalCrossover,
      totalVerge,

      // Kerbs & edgings
      totalKerb,
      totalChannelKerb,
      totalEdgings,
      totalTactiles,

      // Ironwork
      totalGullies,
    };
  };

  const roadsData = state.roads;

  // Always ensure the 8 fixed road types exist in state (write-back on first render
  // or whenever the saved list is missing any of the fixed IDs).
  const savedRoadTypes = roadsData.roadTypes.table || [];
  const mergedRoadTypes: RoadType[] = DEFAULT_ROAD_TYPES.map((def) => {
    const saved = savedRoadTypes.find((rt) => rt.id === def.id);
    return saved || def;
  });
  const savedIds = new Set(savedRoadTypes.map((rt) => rt.id));
  const needsWriteBack = DEFAULT_ROAD_TYPES.some((def) => !savedIds.has(def.id));

  useEffect(() => {
    if (needsWriteBack) {
      updateField("roads", { ...roadsData, roadTypes: { ...roadsData.roadTypes, table: mergedRoadTypes } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsWriteBack]);

  // Auto-calculate derived fields when data changes
  useEffect(() => {
    const updatedRoadTypes = (roadsData.roadTypes.table || []).map((roadType) => {
      // Ensure all required fields exist with defaults (spread first, then apply
      // fallbacks for any missing/falsy values — order matters so the fallback wins)
      const safeRoadType = {
        ...roadType,
        surface: roadType.surface || "",
        surfaceCourse: roadType.surfaceCourse || {
          type: "HRA 55/10",
          thickness: 0,
        },
        binderCourse: roadType.binderCourse || { thickness: 0 },
        baseCourse: roadType.baseCourse || { thickness: 0 },
        subBase: roadType.subBase || { type: "Type 1", thickness: 0 },
        capping: roadType.capping || 0,
      };

      return {
        ...safeRoadType,
        totalConstructionThickness:
          calculateTotalConstructionThickness(safeRoadType),
      };
    });

    // Cut & Fill Sections - preserve existing data without calculations
    const updatedCutFillSections = roadsData.cutFillSections.table || [];

    // REMOVED: All auto-calculations - keeping only existing data
    /*
    const oldUpdatedCutFillSections = (roadsData.cutFillSections.table || []).map(
      (section) => {
        const carriagewayFootway = (roadsData.carriagewayFootways.table || []).find(
          (cf) => cf.id === section.carriagewayFootwayId
        );
        const roadType = updatedRoadTypes.find(
          (rt) => rt.id === section.roadTypeId
        );
        const constructionThickness = roadType
          ? roadType.totalConstructionThickness
          : 0;
        const carriagewayCountForSelectedRoads =
          roadsData.cutFillSections.table.filter(
            (x) => x.carriagewayFootwayId === section.carriagewayFootwayId
          ).length;

        // Ensure all required fields exist with defaults
        const frl = section.frl || section.finishedRoadLevel || 0;
        const existingGroundLevel = section.existingGroundLevel || 0;
        const topsoilStrip = section.topsoilStrip || 0;
        const area = section.area || 0;
        const stripValue = roadsData.cutFillSections.config.stripValueDefault || 0.3;

        // Road formation - use entered value, don't auto-calculate
        const roadFormation = section.roadFormation || 0;

        const roadTotal = roadType ? roadType.totalConstructionThickness : 0;

        const formationLevel = frl - roadTotal;
        const lessStrip = existingGroundLevel - stripValue;
        const depth = lessStrip - formationLevel;

        // Road Area = Selected Carriageway&Footways Total (m²) / Number of counts of Carriageway&Footways
        const carriagewayFootwaysCount = (roadsData.carriagewayFootways.table || [])
          .length;
        console.log("Carriageway", carriagewayFootwaysCount);
        const roadArea =
          carriagewayCountForSelectedRoads > 0
            ? roadFormation / carriagewayCountForSelectedRoads
            : 0;

        // Capping Area = Road Area + (10*0.5*2)
        const cappingArea = roadArea + 10 * 0.5 * 2;

        let cutVolume = 0;
        let fillVolume = 0;

        // Cut = Only when Depth is greater than 0, then Capping Area * Depth
        if (depth > 0) {
          cutVolume = cappingArea * depth;
        }
        // Fill = Only when depth is less than 0, then Capping Area * Depth
        else if (depth < 0) {
          fillVolume = cappingArea * Math.abs(depth);
        }

        // Legacy calculations for compatibility
        const strippedLevel = existingGroundLevel - stripValue;
        const cutFillDepth = lessStrip - formationLevel;

        // Footpaths calculations
        let fpl = 0;
        if (roadType) {
          // For Tarmac Road (FRL + 0.125), For Block Paved Road (FRL + 0.05)
          if (roadType.surface === "Tarmac Road") {
            fpl = frl + 0.125;
          } else if (roadType.surface === "Block Paved Road") {
            fpl = frl + 0.05;
          } else {
            fpl = frl + 0.125; // Default to tarmac
          }
        }

        // Path Form Level calculation
        const totalPath = carriagewayFootway ? carriagewayFootway.totalPath : 0;
        let pathFormLevel = 0;
        if (carriagewayFootway && totalPath > 0) {
          const tarmacPathSum = carriagewayFootway.tarmacPath || 0;
          const blockPavedPathSum = carriagewayFootway.blockPavedPathCrossover || 0;
          const crossoverSum =
            (carriagewayFootway.tarmacCrossover || 0) +
            (carriagewayFootway.heavyDutyCrossover || 0);
          const vergeSum = carriagewayFootway.verge || 0;

          const tarmacFootpathType = 1;
          const blockPavedFootpathType = 1;
          const xoverFootpathType = 1;
          const vergeFootpathType = 1;

          const calculation =
            (tarmacPathSum * tarmacFootpathType +
              blockPavedPathSum * blockPavedFootpathType +
              crossoverSum * xoverFootpathType +
              vergeSum * vergeFootpathType) /
            totalPath;

          pathFormLevel = fpl - calculateTotalBuildUpValue();
        }

        // Footpaths fields - use the same existing ground level as carriageway
        const footpathsLessStrip = existingGroundLevel - stripValue;
        const footpathsDepth = pathFormLevel - footpathsLessStrip;

        // Path Area = Total Path/No. of Chainages
        const numberOfChainages = roadsData.numberOfChainages || 1;
        const pathArea = totalPath / numberOfChainages;

        let footpathsCutVolume = 0;
        let footpathsFillVolume = 0;

        // Cut = Only when depth is greater than 0, then Path Area * Depth
        if (footpathsDepth > 0) {
          footpathsCutVolume = pathArea * footpathsDepth;
        }
        // Fill = Only when depth is less than 0, then Path Area * Depth
        else if (footpathsDepth < 0) {
          footpathsFillVolume = pathArea * Math.abs(footpathsDepth);
        }

        return {
          ...section,
          frl,
          topsoilStrip,
          existingGroundLevel,
          stripValue,
          lessStrip,
          depth,
          roadArea,
          cappingArea,
          roadConstructionThickness: constructionThickness,
          formationLevel,
          roadFormation,
          strippedLevel, // Keep legacy field
          cutFillDepth, // Keep legacy field
          cutVolume,
          fillVolume,
          // Footpaths fields
          fpl,
          pathFormLevel,
          footpathsLessStrip,
          footpathsDepth,
          footpathsCutVolume,
          footpathsFillVolume,
          pathArea,
        };
      }
    );
    */

    const hasRoadTypeChanges =
      JSON.stringify(updatedRoadTypes) !== JSON.stringify(roadsData.roadTypes.table);
    const hasSectionChanges =
      JSON.stringify(updatedCutFillSections) !==
      JSON.stringify(roadsData.cutFillSections.table);

    if (hasRoadTypeChanges || hasSectionChanges) {
      updateField("roads", {
        ...roadsData,
        roadTypes: { ...roadsData.roadTypes, table: updatedRoadTypes },
        cutFillSections: { ...roadsData.cutFillSections, table: updatedCutFillSections },
      });
    }
  }, [
    roadsData.roadTypes.table,
    roadsData.cutFillSections.table,
    roadsData.carriagewayFootways.table,
    roadsData.cutFillSections.config.stripValueDefault,
  ]);

  // Auto-calculate Total BuildUp Value when relevant data changes
  useEffect(() => {
    const newTotalBuildUpValue = calculateTotalBuildUpValue();

    if (
      Math.abs(newTotalBuildUpValue - (roadsData.carriagewayFootways.config.totalBuildUpValue || 0)) >
      0.001
    ) {
      updateField("roads", {
        ...roadsData,
        carriagewayFootways: { ...roadsData.carriagewayFootways, config: { ...roadsData.carriagewayFootways.config, totalBuildUpValue: newTotalBuildUpValue } },
      });
    }
  }, [roadsData.carriagewayFootways.table]);

  const updateRoadType = (id: string, field: keyof RoadType, value: any) => {
    const updatedRoadTypes = mergedRoadTypes.map((rt) =>
      rt.id === id ? { ...rt, [field]: value } : rt
    );
    updateField("roads", { ...roadsData, roadTypes: { ...roadsData.roadTypes, table: updatedRoadTypes } });
  };

  const deleteRoadType = (id: string) => {
    const updatedRoadTypes = (roadsData.roadTypes.table || []).filter(
      (rt) => rt.id !== id
    );
    const updatedCutFillSections = (roadsData.cutFillSections.table || []).filter(
      (section) => section.roadTypeId !== id
    );

    updateField("roads", {
      ...roadsData,
      roadTypes: { ...roadsData.roadTypes, table: updatedRoadTypes },
      cutFillSections: { ...roadsData.cutFillSections, table: updatedCutFillSections },
    });
  };

  // Helper function to get next available chainage, scoped to a single road
  const getNextAvailableChainage = (roadId: string): string => {
    const existingChainages = (roadsData.cutFillSections.table || [])
      .filter((section) => section.carriagewayFootwayId === roadId)
      .map((section) => parseInt(section.chainage))
      .filter((chainage) => !isNaN(chainage))
      .sort((a, b) => a - b);

    // Start from 0 and find the next available chainage in increments of 10
    let nextChainage = 0;
    while (existingChainages.includes(nextChainage)) {
      nextChainage += 10;
    }

    return nextChainage.toString();
  };

  // Helper function to get all chainage options with availability info
  const existingChainageSet = useMemo(
    () =>
      new Set(
        (roadsData.cutFillSections.table || [])
          .map((s) => parseInt(s.chainage))
          .filter((n) => !isNaN(n))
      ),
    [roadsData.cutFillSections.table]
  );

  const getAllChainageOptions = (
    currentChainage?: string
  ): Array<{ value: number; available: boolean; current: boolean }> => {
    const current = currentChainage ? parseInt(currentChainage) : NaN;
    return Array.from({ length: 18 }, (_, i) => i * 10).map((chainage) => ({
      value: chainage,
      available: !existingChainageSet.has(chainage) || chainage === current,
      current: chainage === current,
    }));
  };

  // Helper function to get available chainage options (for backwards compatibility)
  const getAvailableChainageOptions = (currentChainage?: string): number[] => {
    return getAllChainageOptions(currentChainage)
      .filter((option) => option.available)
      .map((option) => option.value);
  };

  // Helper function to validate chainage uniqueness, scoped to a single road
  const validateChainageUniqueness = (
    newChainage: string,
    sectionId: string,
    roadId: string
  ): boolean => {
    const existingChainages = (roadsData.cutFillSections.table || [])
      .filter((section) => section.id !== sectionId && section.carriagewayFootwayId === roadId)
      .map((section) => section.chainage);

    return !existingChainages.includes(newChainage);
  };

  const addCutFillSection = (roadId: string) => {
    const nextChainage = getNextAvailableChainage(roadId);

    const btnEl = cutFillAddChainageBtnRefs.current[roadId];
    if (btnEl) {
      cutFillAnchorRef.current = { roadId, prevTop: btnEl.getBoundingClientRect().top };
    }

    const existingCountForRoad = (roadsData.cutFillSections.table || []).filter(
      (s) => s.carriagewayFootwayId === roadId
    ).length;
    const lastPage = Math.ceil((existingCountForRoad + 1) / CUT_FILL_PAGE_SIZE);
    setCutFillCurrentPage((prev) => ({ ...prev, [roadId]: lastPage }));

    const newSection: CutFillSection = {
      id: Date.now().toString(),
      chainage: nextChainage,
      carriagewayFootwayId: roadId,
      roadTypeId: (roadsData.roadTypes.table || [])[0]?.id || "",
      frl: 0,
      finishedRoadLevel: 0,
      roadConstructionThickness: 0,
      formationLevel: 0,
      existingGroundLevel: 0,
      stripValue: roadsData.cutFillSections.config.stripValueDefault || 0.3,
      lessStrip: 0,
      depth: 0,
      cutVolume: 0,
      fillVolume: 0,
      cappingArea: 0,
      roadArea: 0,
      // Footpaths fields
      fpl: 0,
      pathFormLevel: 0,
      footpathsExistingGroundLevel: 0,
      footpathsLessStrip: 0,
      footpathsDepth: 0,
      footpathsCutVolume: 0,
      footpathsFillVolume: 0,
      pathArea: 0,
    };

    updateField("roads", {
      ...roadsData,
      cutFillSections: { ...roadsData.cutFillSections, table: [...(roadsData.cutFillSections.table || []), newSection] },
    });
  };

  const updateCutFillSectionField = (
    id: string,
    field: keyof CutFillSection,
    value: any
  ) => {
    // Special validation for chainage field
    if (field === "chainage") {
      const section = (roadsData.cutFillSections.table || []).find((s) => s.id === id);
      if (section && !validateChainageUniqueness(value, id, section.carriagewayFootwayId)) {
        alert(
          `Chainage ${value} is already in use for this road. Please select a different value.`
        );
        return;
      }
    }

    const updatedSections = (roadsData.cutFillSections.table || []).map((section) =>
      section.id === id ? { ...section, [field]: value } : section
    );

    updateField("roads", {
      ...roadsData,
      cutFillSections: { ...roadsData.cutFillSections, table: updatedSections },
    });
  };

  const deleteCutFillSection = (id: string) => {
    const updatedSections = (roadsData.cutFillSections.table || []).filter(
      (section) => section.id !== id
    );

    updateField("roads", {
      ...roadsData,
      cutFillSections: { ...roadsData.cutFillSections, table: updatedSections },
    });
  };

  const [selectedCutFillKeys, setSelectedCutFillKeys] = useState<React.Key[]>([]);

  const deleteCutFillSections = (ids: React.Key[]) => {
    const idSet = new Set(ids);
    const updatedSections = (roadsData.cutFillSections.table || []).filter(
      (section) => !idSet.has(section.id)
    );

    updateField("roads", {
      ...roadsData,
      cutFillSections: { ...roadsData.cutFillSections, table: updatedSections },
    });
  };

  const updateCappingLayer = (field: keyof CappingLayer, value: any) => {
    updateField("roads", {
      ...roadsData,
      capping: {
        ...roadsData.capping,
        config: {
          ...roadsData.capping.config,
          cappingLayer: {
            ...roadsData.capping.config.cappingLayer,
            [field]: value,
          },
        },
      },
    });
  };

  // Capping Layer tab — dynamic sections. Falls back to the legacy fixed
  // 150/350/600mm sections when no sections have been added yet.
  const cappingThicknesses = [...(roadsData.capping.config.cappingLayer.thicknesses?.length
    ? roadsData.capping.config.cappingLayer.thicknesses
    : DEFAULT_CAPPING_THICKNESSES)].sort((a, b) => a - b);

  const [addCappingModalVisible, setAddCappingModalVisible] = useState(false);
  const [newCappingThicknessMm, setNewCappingThicknessMm] = useState<number | null>(null);

  const openAddCappingSection = () => {
    setNewCappingThicknessMm(null);
    setAddCappingModalVisible(true);
  };

  const confirmAddCappingSection = () => {
    if (!newCappingThicknessMm || newCappingThicknessMm <= 0) return;
    if (!cappingThicknesses.includes(newCappingThicknessMm)) {
      updateCappingLayer("thicknesses", [...cappingThicknesses, newCappingThicknessMm]);
    }
    setAddCappingModalVisible(false);
  };

  const deleteCappingSection = (mm: number) => {
    updateCappingLayer("thicknesses", cappingThicknesses.filter((t) => t !== mm));
  };

  // Capping Layer section Configure gear — same per-section draft/Apply pattern as
  // POS.tsx's openSectionConfig/applySectionConfig, but for the two "Omit xxmm thick
  // Type 1 to tarmac/block paved road" line items' thickness override.
  const t1ThicknessMm = roadsData.capping.config.cappingLayer.t1ThicknessMm ?? {};
  const [cappingConfigModalVisible, setCappingConfigModalVisible] = useState(false);
  const [cappingConfigMm, setCappingConfigMm] = useState<number | null>(null);
  const [cappingConfigDraft, setCappingConfigDraft] = useState<{ tarmac: number; block: number }>({ tarmac: 0, block: 0 });
  const openCappingSectionConfig = (mm: number) => {
    setCappingConfigMm(mm);
    setCappingConfigDraft({
      tarmac: t1ThicknessMm[`${mm}-tarmac`] ?? 0,
      block: t1ThicknessMm[`${mm}-block`] ?? 0,
    });
    setCappingConfigModalVisible(true);
  };
  const applyCappingSectionConfig = () => {
    if (cappingConfigMm === null) return;
    const mm = cappingConfigMm;
    const next = { ...t1ThicknessMm };
    if (cappingConfigDraft.tarmac > 0) next[`${mm}-tarmac`] = cappingConfigDraft.tarmac;
    else delete next[`${mm}-tarmac`];
    if (cappingConfigDraft.block > 0) next[`${mm}-block`] = cappingConfigDraft.block;
    else delete next[`${mm}-block`];
    updateCappingLayer("t1ThicknessMm", next);
    setCappingConfigModalVisible(false);
  };

  // Road Types — transposed (row-wise) table helpers
  const renderRoadTypesTable = () => {
    const roadTypes = mergedRoadTypes;

    const thStyle: React.CSSProperties = {
      backgroundColor: COLORS.tableHeaderBg,
      border: '1px solid #e5e7eb',
      padding: '8px 6px',
      textAlign: 'center' as const,
      fontWeight: 600,
      fontSize: 12,
    };

    const labelStyle: React.CSSProperties = {
      backgroundColor: '#fafafa',
      border: '1px solid #e5e7eb',
      padding: '8px 10px',
      fontWeight: 500,
      fontSize: 13,
      whiteSpace: 'nowrap' as const,
      width: '190px',
    };

    // Attached items — checkbox multi-select (leave empty for none)
    const renderAttachSelect = (field: 'baseAttachments' | 'binderAttachments') => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 44, flexShrink: 0 }}>Attach</span>
        <Select
          mode="multiple"
          size="small"
          placeholder="Select"
          value={(roadsData.capping.config[field] ?? []).filter((v) => v !== BASE_ATTACHMENT_NONE)}
          onChange={(vals: BaseAttachmentCode[]) => {
            const picked = vals.filter((v) => v !== BASE_ATTACHMENT_NONE);
            updateField('roads', {
              ...roadsData,
              capping: { ...roadsData.capping, config: { ...roadsData.capping.config, [field]: picked.length > 0 ? picked : undefined } },
            });
          }}
          options={BASE_ATTACHMENT_OPTIONS.map((o) => ({ value: o.c, label: o.n }))}
          optionRender={(opt) => (
            <Checkbox
              checked={(roadsData.capping.config[field] ?? []).includes(opt.value as BaseAttachmentCode)}
              style={{ pointerEvents: 'none' }}
            >
              {opt.label}
            </Checkbox>
          )}
          menuItemSelectedIcon={null}
          maxTagCount="responsive"
          style={{ flex: 1, minWidth: 0 }}
        />
        </div>
    );

    const cellStyle: React.CSSProperties = {
      border: '1px solid #e5e7eb',
      padding: '5px 6px',
      textAlign: 'center' as const,
      verticalAlign: 'middle' as const,
    };

    const subCellStyle: React.CSSProperties = {
      ...cellStyle,
      backgroundColor: '#fafafa',
    };

    return (
      <div>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '190px' }} />
            {roadTypes.map((rt) => (
              <col key={rt.id} style={{ width: `calc((100% - 190px) / ${roadTypes.length})` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...labelStyle, textAlign: 'left' as const }} />
              {roadTypes.map((rt) => (
                <th key={rt.id} style={thStyle}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>
                    {DEFAULT_ROAD_TYPES.find((d) => d.id === rt.id)?.name ?? rt.name}
                  </span>
                </th>
              ))}
            </tr>
            <tr>
              <th style={{ ...labelStyle, textAlign: 'left' as const }} />
              {roadTypes.map((rt) => (
                <th key={rt.id} style={{ ...thStyle, fontSize: 11, color: '#6B7280', fontWeight: 400, padding: '2px 8px' }}>
                  m
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Name */}
            <tr>
              <td style={labelStyle}>Name</td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={cellStyle}>
                  <Input
                    value={rt.name}
                    onChange={(e) => updateRoadType(rt.id, 'name', e.target.value)}
                    size="small"
                    style={{ width: '100%' }}
                  />
                </td>
              ))}
            </tr>
            {/* Membrane */}
            <tr>
              <td style={labelStyle}>Membrane</td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={cellStyle}>
                  <Select
                    value={rt.membrane ? 'Yes' : 'No'}
                    onChange={(v) => updateRoadType(rt.id, 'membrane', v === 'Yes')}
                    size="small"
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="No">No</Select.Option>
                    <Select.Option value="Yes">Yes</Select.Option>
                  </Select>
                </td>
              ))}
            </tr>
            {/* Capping */}
            <tr>
              <td style={{ ...labelStyle, padding: '2px 4px' }}>
                <Select
                  value={roadsData.capping.config.cappingType ?? 'R/C 6F2 capping'}
                  onChange={(v: CappingType) => updateField('roads', {
                    ...roadsData,
                    capping: { ...roadsData.capping, config: { ...roadsData.capping.config, cappingType: v } },
                  })}
                  size="small"
                  style={{ width: '100%' }}
                >
                  {CAPPING_TYPES.map((t) => (
                    <Select.Option key={t} value={t}>
                      {t}
                    </Select.Option>
                  ))}
                </Select>
              </td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={cellStyle}>
                  <NumberInput
                    value={rt.capping ?? 0}
                    onChange={(v) => updateRoadType(rt.id, 'capping', v)}
                    decimalPlaces={3}
                    size="small"
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
              ))}
            </tr>
            {/* Sub Base */}
            <tr>
              <td style={labelStyle}>Sub Base</td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={cellStyle}>
                  <NumberInput
                    value={rt.subBase.thickness ?? 0}
                    onChange={(v) =>
                      updateRoadType(rt.id, 'subBase', { ...rt.subBase, thickness: v })
                    }
                    decimalPlaces={3}
                    size="small"
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
              ))}
            </tr>
            {/* Base */}
            <tr>
              <td style={{ ...labelStyle, padding: '2px 4px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 44, flexShrink: 0 }}>Base</span>
                    <Select
                      value={roadsData.capping.config.baseCourseType ?? ''}
                      onChange={(v: BaseCourseType | '') => updateField('roads', {
                        ...roadsData,
                        capping: { ...roadsData.capping, config: { ...roadsData.capping.config, baseCourseType: v || undefined } },
                      })}
                      size="small"
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <Select.Option value="">Select</Select.Option>
                      {BASE_COURSE_OPTIONS.map((o) => (
                        <Select.Option key={o.c} value={o.n}>
                          {o.n}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  {renderAttachSelect('baseAttachments')}
                </div>
              </td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={cellStyle}>
                  <NumberInput
                    value={rt.baseCourse.thickness ?? 0}
                    onChange={(v) =>
                      updateRoadType(rt.id, 'baseCourse', { thickness: v })
                    }
                    decimalPlaces={3}
                    size="small"
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
              ))}
            </tr>
            {/* Binder */}
            <tr>
              <td style={{ ...labelStyle, padding: '2px 4px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 44, flexShrink: 0 }}>Binder</span>
                <Select
                  value={roadsData.capping.config.binderCourseType ?? ''}
                  onChange={(v: BinderCourseType | '') => updateField('roads', {
                    ...roadsData,
                    capping: { ...roadsData.capping, config: { ...roadsData.capping.config, binderCourseType: v || undefined } },
                  })}
                  size="small"
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <Select.Option value="">Select</Select.Option>
                  {BINDER_COURSE_OPTIONS.map((o) => (
                    <Select.Option key={o.c} value={o.n}>
                      {o.n}
                    </Select.Option>
                  ))}
                </Select>
                </div>
                {renderAttachSelect('binderAttachments')}
                </div>
              </td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={cellStyle}>
                  <NumberInput
                    value={rt.binderCourse.thickness ?? 0}
                    onChange={(v) =>
                      updateRoadType(rt.id, 'binderCourse', { thickness: v })
                    }
                    decimalPlaces={3}
                    size="small"
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
              ))}
            </tr>
            {/* Surface thickness (Tarmac only) */}
            <tr>
              <td style={{ ...labelStyle, padding: '2px 4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 44, flexShrink: 0 }}>Surface</span>
                  <Select
                    value={roadsData.capping.config.surfaceCourseGroup ?? ''}
                    onChange={(v: SurfaceCourseGroup | '') => {
                      const allowed: string[] = v ? SURFACE_COURSE_OPTIONS_BY_GROUP[v].map((o) => o.n) : [];
                      updateField('roads', {
                        ...roadsData,
                        roadTypes: {
                          ...roadsData.roadTypes,
                          table: mergedRoadTypes.map((rt) =>
                            rt.surface === 'Tarmac Road' && v && !allowed.includes(rt.surfaceCourse.type)
                              ? { ...rt, surfaceCourse: { ...rt.surfaceCourse, type: '' } }
                              : rt,
                          ),
                        },
                        capping: { ...roadsData.capping, config: { ...roadsData.capping.config, surfaceCourseGroup: v || undefined } },
                      });
                    }}
                    size="small"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Select.Option value="">Select</Select.Option>
                    {SURFACE_COURSE_GROUPS.map((g) => (
                      <Select.Option key={g} value={g}>
                        {g}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={rt.surface === 'Tarmac Road' ? cellStyle : { ...cellStyle, backgroundColor: '#f0f0f0' }}>
                  {rt.surface === 'Tarmac Road' ? (
                    <NumberInput
                      value={rt.surfaceCourse.thickness ?? 0}
                      onChange={(v) =>
                        updateRoadType(rt.id, 'surfaceCourse', {
                          ...rt.surfaceCourse,
                          thickness: v,
                        })
                      }
                      decimalPlaces={3}
                      size="small"
                      style={{ width: '100%', textAlign: 'right' }}
                    />
                  ) : null}
                </td>
              ))}
            </tr>
            {/* Surface course type sub-row */}
            <tr>
              <td style={{ ...labelStyle, color: '#9CA3AF', fontSize: 12 }} />
              {roadTypes.map((rt) => (
                <td key={rt.id} style={subCellStyle}>
                  <Select
                    value={rt.surfaceCourse.type}
                    onChange={(v) =>
                      updateRoadType(rt.id, 'surfaceCourse', {
                        ...rt.surfaceCourse,
                        type: v,
                      })
                    }
                    size="small"
                    style={{ width: '100%' }}
                  >
                    {(rt.surface === 'Block Paved Road'
                      ? BLOCK_PAVED_SURFACE_COURSE_TYPES
                      : roadsData.capping.config.surfaceCourseGroup
                        ? SURFACE_COURSE_OPTIONS_BY_GROUP[roadsData.capping.config.surfaceCourseGroup].map((o) => o.n)
                        : TARMAC_SURFACE_COURSE_TYPES
                    ).map((t) => (
                      <Select.Option key={t} value={t}>
                        {t}
                      </Select.Option>
                    ))}
                  </Select>
                </td>
              ))}
            </tr>
            {/* EO Pre coat (Tarmac only) */}
            <tr>
              <td style={labelStyle}>EO Pre coat</td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={rt.surface === 'Tarmac Road' ? cellStyle : { ...cellStyle, backgroundColor: '#f0f0f0' }}>
                  {rt.surface === 'Tarmac Road' ? (
                    <Select
                      value={rt.eoPreCoat ? 'Yes' : 'No'}
                      onChange={(v) => updateRoadType(rt.id, 'eoPreCoat', v === 'Yes')}
                      size="small"
                      style={{ width: '100%' }}
                    >
                      <Select.Option value="No">No</Select.Option>
                      <Select.Option value="Yes">Yes</Select.Option>
                    </Select>
                  ) : null}
                </td>
              ))}
            </tr>
            {/* EO PSV 65+ (Tarmac only) */}
            <tr>
              <td style={labelStyle}>EO PSV 65+</td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={rt.surface === 'Tarmac Road' ? cellStyle : { ...cellStyle, backgroundColor: '#f0f0f0' }}>
                  {rt.surface === 'Tarmac Road' ? (
                    <Select
                      value={rt.eoPSV65 ? 'Yes' : 'No'}
                      onChange={(v) => updateRoadType(rt.id, 'eoPSV65', v === 'Yes')}
                      size="small"
                      style={{ width: '100%' }}
                    >
                      <Select.Option value="No">No</Select.Option>
                      <Select.Option value="Yes">Yes</Select.Option>
                    </Select>
                  ) : null}
                </td>
              ))}
            </tr>
            {/* Sand / Pave (Block Paved only) */}
            <tr>
              <td style={labelStyle}>Sand / Pave</td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={rt.surface === 'Block Paved Road' ? cellStyle : { ...cellStyle, backgroundColor: '#f0f0f0' }}>
                  {rt.surface === 'Block Paved Road' ? (
                    <NumberInput
                      value={rt.sandPave ?? 0}
                      onChange={(v) => updateRoadType(rt.id, 'sandPave', v)}
                      decimalPlaces={3}
                      size="small"
                      style={{ width: '100%', textAlign: 'right' }}
                    />
                  ) : null}
                </td>
              ))}
            </tr>
            {/* Total */}
            <tr>
              <td style={{ ...labelStyle, fontWeight: 600 }}>Total (m)</td>
              {roadTypes.map((rt) => (
                <td key={rt.id} style={{ ...cellStyle, backgroundColor: '#f0f9ff' }}>
                  <Tag color="blue" style={{ width: '100%', textAlign: 'center', margin: 0 }}>
                    {Number(rt.totalConstructionThickness).toFixed(3)}
                  </Tag>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Group cut fill sections by road — memoised so the loop only runs when sections change.
  const cutFillRoadGroups = useMemo(
    () => groupCutFillSectionsByRoad(roadsData.cutFillSections.table || [], roadsData.carriagewayFootways.table || []),
    [roadsData.cutFillSections.table, roadsData.carriagewayFootways.table]
  );

  // Cut & Fill tab: same road groups, with Road Construction/Formation Level/Less Strip/
  // Depth/Road Area/Capping Area/Cut Vol/Fill Vol derived per the Excel formulas
  // (see computeCutFillFields) rather than stored on the section.
  const cutFillCalculatedRoadGroups = useMemo(
    () =>
      computeLiveCutFillGroups(
        cutFillRoadGroups,
        roadsData.carriagewayFootways.table || [],
        mergedRoadTypes,
        roadsData.cutFillSections.config.stripValueDefault ?? 0.3,
        roadsData.carriagewayFootways.config.kerbRaceWidth ?? 0.5
      ),
    [
      cutFillRoadGroups,
      roadsData.carriagewayFootways.table,
      roadsData.cutFillSections.config.stripValueDefault,
      roadsData.carriagewayFootways.config.kerbRaceWidth,
      mergedRoadTypes,
    ]
  );

  // `roadsData.footpaths.config` is typed as `FootpathsConstructionConfig | Record<string, never>`
  // (empty object before it's ever been configured) — fall back to the default build-up
  // whenever the saved config hasn't actually been populated yet.
  const resolvedFootpathsConfig: FootpathsConstructionConfig =
    (roadsData.footpaths.config as FootpathsConstructionConfig)?.tarmacPath !== undefined
      ? (roadsData.footpaths.config as FootpathsConstructionConfig)
      : DEFAULT_FOOTPATHS_CONFIG;

  const footpathsAverageBuildUp = useMemo(
    () => calculateFootpathsAverageBuildUp(resolvedFootpathsConfig),
    [resolvedFootpathsConfig]
  );

  // Footpaths tab: same road groups as Cut & Fill, with the Footpath Level/Path Formation
  // Level/Less Strip/Depth/Cut Vol/Fill Vol/Path Area fields derived per the Excel formulas
  // (see computeFootpathsFields) rather than stored on the section.
  const footpathsCutFillRoadGroups = useMemo(
    () =>
      computeLiveFootpathsGroups(
        cutFillRoadGroups,
        roadsData.carriagewayFootways.table || [],
        roadsData.cutFillSections.config.stripValueDefault ?? 0.3,
        footpathsAverageBuildUp
      ),
    [
      cutFillRoadGroups,
      roadsData.carriagewayFootways.table,
      roadsData.cutFillSections.config.stripValueDefault,
      footpathsAverageBuildUp,
    ]
  );

  const colHeaderCalc = (
    title: string,
    unit: string,
    formula: string
  ): React.ReactNode => (
    <CfColTitle
      title={title}
      unit={unit}
      onInfoClick={showDevMode ? () => setColCalcInfo({ title, formula }) : undefined}
    />
  );

  const cutFillColumns = useMemo(() => [
    {
      title: <CfColTitle title="Road Name" />,
      dataIndex: "carriagewayFootwayId",
      key: "carriagewayFootwayId",
      width: 140,
      fixed: "left" as const,
      render: (text: string) => (
        <span>
          {roadsData.carriagewayFootways.table?.find((cf) => cf.id === text)?.roadName ?? "—"}
        </span>
      ),
    },
    {
      title: <CfColTitle title="Chainage" />,
      dataIndex: "chainage",
      key: "chainage",
      width: 130,
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: <CfColTitle title="FRL" unit="m" />,
      dataIndex: "frl",
      key: "frl",
      width: 100,
      align: "right" as const,
      render: (text: number, record: CutFillSection) => (
        <NumberInput
          value={text ?? 0}
          onChange={(value) =>
            updateCutFillSectionField(record.id, "frl", value)
          }
          decimalPlaces={3}
          size="small"
          style={{ width: "100%", textAlign: "right" }}
        />
      ),
    },
    {
      title: <CfColTitle title="Road Type" />,
      dataIndex: "roadTypeId",
      key: "roadTypeId",
      width: 130,
      render: (text: string, record: CutFillSection) => (
        <Select
          value={text}
          onChange={(value) =>
            updateCutFillSectionField(record.id, "roadTypeId", value)
          }
          placeholder="Select type"
          size="small"
          style={{ width: "100%" }}
        >
          {(roadsData.roadTypes.table || []).map((rt) => (
            <Select.Option key={rt.id} value={rt.id}>
              {rt.name}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: colHeaderCalc(
        "Road Construction",
        "mm",
        "Road Construction = Selected Road Type's total layer thickness (Surface + Binder + Base + Sub Base + Capping)"
      ),
      dataIndex: "roadConstructionThickness",
      key: "roadConstructionThickness",
      width: 145,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => {
        const rt = (roadsData.roadTypes.table || []).find(
          (r) => r.id === record.roadTypeId
        );
        const val = rt ? rt.totalConstructionThickness : 0;
        return (
          <span style={{ fontWeight: 600, color: COLORS.primary }}>
            {(val || 0).toFixed(3)}
          </span>
        );
      },
    },
    {
      title: colHeaderCalc(
        "Formation Level",
        "m",
        "Formation Level = FRL - Road Construction"
      ),
      dataIndex: "formationLevel",
      key: "formationLevel",
      width: 140,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.formationLevel || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: <CfColTitle title="Extg Ground" unit="m" />,
      dataIndex: "existingGroundLevel",
      key: "existingGroundLevel",
      width: 120,
      align: "right" as const,
      render: (text: number, record: CutFillSection) => (
        <NumberInput
          value={text ?? 0}
          onChange={(value) =>
            updateCutFillSectionField(record.id, "existingGroundLevel", value)
          }
          decimalPlaces={3}
          size="small"
          style={{ width: "100%", textAlign: "right" }}
        />
      ),
    },
    {
      title: colHeaderCalc(
        "Less Strip",
        "m",
        "Less Strip = Existing Ground Level - Strip Value"
      ),
      dataIndex: "lessStrip",
      key: "lessStrip",
      width: 110,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.lessStrip || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Depth",
        "m",
        "Depth = Less Strip - Formation Level"
      ),
      dataIndex: "depth",
      key: "depth",
      width: 100,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.depth || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Capping Area",
        "m²",
        "Capping Area = 0 if Road Area = 0, else Road Area + (10 × Kerb Race Width × 2) — Kerb Race Width set on Kerbs & Edgings tab's Configure modal"
      ),
      dataIndex: "cappingArea",
      key: "cappingArea",
      width: 125,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.cappingArea || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Road Area",
        "m²",
        "Road Area = Total Road Area (Carriageway Take-off) ÷ No. of Chainages for this road"
      ),
      dataIndex: "roadArea",
      key: "roadArea",
      width: 110,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.roadArea || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Cut Vol",
        "m³",
        "Cut Vol = 0 if Road Area = 0; else (Depth × Capping Area, only when Depth > 0) + (0.5 × Depth² × (Next Chainage − Current Chainage) × 2, only when Depth ≥ 0.5) — Next Chainage is the chainage value of the row immediately after Current Chainage (this row's own chainage)"
      ),
      dataIndex: "cutVolume",
      key: "cutVolume",
      width: 105,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: "#D46B08" }}>
          {(record.cutVolume || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Fill Vol",
        "m³",
        "Fill Vol (as shown here, matching the Excel Fill Vol cell) = 0 if Road Area = 0; else −[(|Depth| × Capping Area, only when Depth < 0) + (0.5 × Depth² × (Next Chainage − Current Chainage) × 2, only when Depth ≤ -0.5)] — Next Chainage is the chainage value of the row immediately after Current Chainage (this row's own chainage), same neighbour used by Cut Vol — stored as a positive magnitude for Bill of Quantities calculations elsewhere"
      ),
      dataIndex: "fillVolume",
      key: "fillVolume",
      width: 105,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: "#389E0D" }}>
          {(-(record.fillVolume || 0)).toFixed(3)}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 75,
      fixed: "right" as const,
      align: 'center' as const,
      render: (_: unknown, record: CutFillSection) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => requestDelete(() => deleteCutFillSection(record.id))}
        />
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [roadsData.roadTypes.table, roadsData.carriagewayFootways.table, existingChainageSet, updateCutFillSectionField, deleteCutFillSection]);

  // Footpaths table columns (read-only calculated values from cut fill sections)
  const footpathsColumns = useMemo(() => [
    {
      title: <CfColTitle title="Road Name" />,
      dataIndex: "carriagewayFootwayId",
      key: "carriagewayFootwayId",
      width: 140,
      fixed: "left" as const,
      render: (text: string) => {
        const cf = (roadsData.carriagewayFootways.table || []).find((r) => r.id === text);
        return <span style={{ fontWeight: 500 }}>{cf?.roadName || "—"}</span>;
      },
    },
    {
      title: <CfColTitle title="Chainage" />,
      dataIndex: "chainage",
      key: "chainage",
      width: 100,
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: colHeaderCalc(
        "Footpath Level",
        "m",
        "Footpath Level = FRL + 0.125"
      ),
      dataIndex: "fpl",
      key: "fpl",
      width: 130,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.fpl || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Path Formation Level",
        "m",
        "Path Formation Level = Footpath Level - Average Total build-up (Footpaths Config)"
      ),
      dataIndex: "pathFormLevel",
      key: "pathFormLevel",
      width: 160,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.pathFormLevel || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Less Strip",
        "m",
        "Less Strip = Existing Ground Level - Strip Value"
      ),
      dataIndex: "footpathsLessStrip",
      key: "footpathsLessStrip",
      width: 115,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.footpathsLessStrip || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Depth",
        "m",
        "Depth = Less Strip - Path Formation Level"
      ),
      dataIndex: "footpathsDepth",
      key: "footpathsDepth",
      width: 100,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.footpathsDepth || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Cut Vol",
        "m³",
        "Cut Vol = Path Area × Depth, only when Depth > 0; otherwise 0"
      ),
      dataIndex: "footpathsCutVolume",
      key: "footpathsCutVolume",
      width: 105,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: "#D46B08" }}>
          {(record.footpathsCutVolume || 0).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Fill Vol",
        "m³",
        "Fill Vol (as shown here, matching the Excel Fill Vol cell) = −(Path Area × |Depth|), only when Depth < 0; otherwise 0 — stored as a positive magnitude for Bill of Quantities calculations elsewhere"
      ),
      dataIndex: "footpathsFillVolume",
      key: "footpathsFillVolume",
      width: 105,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: "#389E0D" }}>
          {(-(record.footpathsFillVolume || 0)).toFixed(3)}
        </span>
      ),
    },
    {
      title: colHeaderCalc(
        "Path Area",
        "m²",
        "Path Area = Total Path (Carriageway & Footways) ÷ No. of Chainages"
      ),
      dataIndex: "pathArea",
      key: "pathArea",
      width: 110,
      align: "right" as const,
      render: (_: number, record: CutFillSection) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.pathArea || 0).toFixed(3)}
        </span>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [roadsData.carriagewayFootways.table]);

  // Combined Carriageway & Footways handlers. Roads are equivalent across every
  // tab (Carriageway Take Off, Kerbs & Edgings, Footways all view this same
  // `carriagewayFootways` array), and Carriageway Cut & Fill / Footpaths sections
  // are keyed to a road via `carriagewayFootwayId` — so whenever a road disappears
  // from the next array (deleted from any of those three tabs, bulk or per-row),
  // cascade-delete its orphaned Cut & Fill / Footpaths sections here too.
  const updateCarriagewayFootways = (
    carriagewayFootways: CarriagewayFootway[]
  ) => {
    const nextIds = new Set(carriagewayFootways.map((r) => r.id));
    const removedIds = (roadsData.carriagewayFootways.table || [])
      .map((r) => r.id)
      .filter((id) => !nextIds.has(id));

    updateField("roads", {
      ...roadsData,
      carriagewayFootways: { ...roadsData.carriagewayFootways, table: carriagewayFootways },
      ...(removedIds.length > 0
        ? {
            cutFillSections: {
              ...roadsData.cutFillSections,
              table: (roadsData.cutFillSections.table || []).filter(
                (s) => !removedIds.includes(s.carriagewayFootwayId)
              ),
            },
          }
        : {}),
    });
  };

  const addCarriagewayFootway = () => {
    const newRecord: CarriagewayFootway = {
      id: Date.now().toString(),
      roadName: `Road ${(roadsData.carriagewayFootways.table || []).length + 1}`,
      // Carriageway Take Off fields
      tarmacSpec1: 0, tarmacSpec2: 0, tarmacSpec3: 0, tarmacSpec4: 0,
      blockPavedSpec1: 0, blockPavedSpec2: 0, blockPavedSpec3: 0, blockPavedSpec4: 0,
      totalRoadArea: 0,
      kerbRace: 0, dowelBarsInRace: 0, keyChannel: 0, filletToCrossover: 0,
      removeFillet: 0, extrudedKerbRace: 0, tempKerbBlock: 0, removeTempKerbBlock: 0,
      kerb10x5IncRace: 0, kerb10x5OnRace: 0, kerb6x5OnRace: 0, dropKerb: 0,
      delineationKerb: 0, eoRadiusKerb: 0, eoRadiusDropKerb: 0, kasselKerb: 0,
      triefKerb: 0, beanyKerb: 0, beanyGully: 0, acoKerbDrain: 0, acoKerbGully: 0,
      eoRamps: 0,
      // Footways fields
      tarmacPath: 0, tarmacCrossover: 0, heavyDutyCrossover: 0,
      blockPavedPathCrossover: 0, cycleway: 0, verge: 0, totalPath: 0,
      edgings150x50: 0, edgings200x50: 0, tactilePaving: 0, corduroyPaving: 0,
    };
    updateCarriagewayFootways([
      ...(roadsData.carriagewayFootways.table || []),
      newRecord,
    ]);
  };

  const [selectedCarriagewayFootwayKeys, setSelectedCarriagewayFootwayKeys] = useState<React.Key[]>([]);
  const [selectedFootwaysTakeoffKeys, setSelectedFootwaysTakeoffKeys] = useState<React.Key[]>([]);
  const [selectedKerbsEdgingsKeys, setSelectedKerbsEdgingsKeys] = useState<React.Key[]>([]);

  // Carriageway/Footways/Kerbs & Edgings tabs are three different column views
  // over the same underlying `carriagewayFootways` array, so one delete
  // function (keyed by row id) works for all three tabs' bulk deletes.
  const deleteCarriagewayFootwayRows = (ids: React.Key[]) => {
    const idSet = new Set(ids);
    updateCarriagewayFootways(
      (roadsData.carriagewayFootways.table || []).filter((item) => !idSet.has(item.id))
    );
  };

  // Calculate Total BuildUp Value
  // NOTE: the `footwayConfigurations` RoadsData field (and its dead CRUD handlers)
  // were removed as part of the Roads persisted-data restructure — this calculation
  // always returned a fixed factor regardless of input, so behaviour is unchanged.
  const calculateTotalBuildUpValue = () => {
    return 0.338;
  };

  // Unified Excel Export
  const handleUnifiedExport = async () => {
    try {
      const data: RoadsExcelData = {
        roadTypes: roadsData.roadTypes.table || [],
        cutFillSections: roadsData.cutFillSections.table || [],
        carriagewayFootways: roadsData.carriagewayFootways.table || [],
        // Footway Configurations tab/field was removed from RoadsData (dead in the live UI) —
        // exported as empty for DTO shape compatibility only.
        footwayConfigurations: [],
        cappingLayer: roadsData.capping.config.cappingLayer || {
          enabled: false,
          thicknesses: [],
        },
        stripValueDefault: roadsData.cutFillSections.config.stripValueDefault || 0.3,
        // Number of Chainages was removed from RoadsData (dead field) — kept as a fixed
        // legacy default for the exported "Configuration" summary sheet only.
        numberOfChainages: 10,
        totalBuildUpValue: roadsData.carriagewayFootways.config.totalBuildUpValue || 0,
      };

      await exportRoadsToExcel(data, state.projectName || "Roads");
    } catch (error) {
      console.error("Export failed:", error);
      alert(`Export failed: ${error.message}`);
    }
  };

  // Generate Template
  const handleDownloadTemplate = async () => {
    try {
      await generateRoadsTemplate(state.projectName || "Roads");
    } catch (error) {
      console.error("Template generation failed:", error);
      alert(`Template generation failed: ${error.message}`);
    }
  };

  // Force recalculation of all fields after import
  const recalculateAllFields = (data: any) => {
    // Recalculate road types with total construction thickness
    const recalculatedRoadTypes = (data.roadTypes || []).map(
      (roadType: any) => {
        const safeRoadType = {
          surface: roadType.surface || "",
          surfaceCourse: roadType.surfaceCourse || {
            type: "HRA 55/10",
            thickness: 0,
          },
          binderCourse: roadType.binderCourse || { thickness: 0 },
          baseCourse: roadType.baseCourse || { thickness: 0 },
          subBase: roadType.subBase || { type: "Type 1", thickness: 0 },
          capping: roadType.capping || 0,
          ...roadType,
        };

        return {
          ...safeRoadType,
          totalConstructionThickness:
            calculateTotalConstructionThickness(safeRoadType),
        };
      }
    );

    // Cut-fill sections will be calculated AFTER total build up value is computed

    // Recalculate carriageway & footways totals first
    const recalculatedCarriagewayFootways = (
      data.carriagewayFootways || []
    ).map((item: any) => {
      const totalRoadArea =
        (item.tarmacSpec1 || 0) + (item.tarmacSpec2 || 0) +
        (item.tarmacSpec3 || 0) + (item.tarmacSpec4 || 0) +
        (item.blockPavedSpec1 || 0) + (item.blockPavedSpec2 || 0) +
        (item.blockPavedSpec3 || 0) + (item.blockPavedSpec4 || 0);
      const totalPath =
        (item.tarmacPath || 0) +
        (item.tarmacCrossover || 0) +
        (item.heavyDutyCrossover || 0) +
        (item.blockPavedPathCrossover || 0) +
        (item.cycleway || 0) +
        (item.verge || 0);
      return {
        ...item,
        totalRoadArea,
        totalPath,
      };
    });

    // Recalculate footways configuration totals
    const recalculatedFootwayConfigs = (data.footwayConfigurations || []).map(
      (config: any) => {
        const total =
          (config.surface || 0) +
          (config.binder || 0) +
          (config.base || 0) +
          (config.type1 || 0);
        return {
          ...config,
          total: total,
        };
      }
    );

    // Calculate total build up value using corrected formula
    const totalPath = recalculatedCarriagewayFootways.reduce(
      (total: number, item: any) => total + (item.totalPath || 0),
      0
    );

    // Get current buildup value from Footway Configurations
    const currentBuildUpValue = recalculatedFootwayConfigs.reduce(
      (total: number, config: any) => {
        const configTotal = config.total || 0;
        let buildUpFactor = 0;

        switch (config.name) {
          case "Xover":
            buildUpFactor = 0.15;
            break;
          case "Verge":
            buildUpFactor = 0.15;
            break;
          case "Tarmac":
            buildUpFactor = 0.15;
            break;
          case "Block Paved":
            buildUpFactor = 0.25;
            break;
        }

        return total + configTotal * buildUpFactor;
      },
      0
    );

    // Calculate Total BuildUp Value = current buildup value / total path
    let recalculatedTotalBuildUpValue = 0;
    if (totalPath > 0) {
      recalculatedTotalBuildUpValue = currentBuildUpValue / totalPath;
    }

    // Cut & Fill Sections - use imported data as-is without calculations
    const recalculatedCutFillSections = data.cutFillSections || [];

    // REMOVED: All auto-calculations - keeping only imported data
    /*
    const recalculatedCutFillSections = (data.cutFillSections || []).map(
      (section: any) => {
        const existingGroundLevel = section.existingGroundLevel || 0;
        const stripValue = data.stripValueDefault || 0.3;
        const totalBuildUpValue = recalculatedTotalBuildUpValue; // Use calculated value

        // Find corresponding road type and carriageway footway
        const roadType =
          recalculatedRoadTypes.find(
            (rt: any) => rt.id === section.roadTypeId
          ) || recalculatedRoadTypes[0];
        const carriagewayFootway =
          recalculatedCarriagewayFootways.find(
            (cf: any) => cf.id === section.carriagewayFootwayId
          ) || recalculatedCarriagewayFootways[0];

        // Basic calculations
        const carriagewayChainage = section.carriagewayChainage || 0;
        const carriageWayWidth = section.carriageWayWidth || 0;
        const carriageway = carriagewayChainage * carriageWayWidth;

        const footwayChainage = section.footwayChainage || 0;
        const footwayWidth = section.footwayWidth || 0;
        const footway = footwayChainage * footwayWidth;

        // Carriageway calculations - use imported FRL value correctly
        const topsoilStrip = section.topsoilStrip || 0;
        const frl = section.frl || section.finishedRoadLevel || 0; // Use imported FRL value
        const roadTotal = roadType ? roadType.totalConstructionThickness : 0;
        
        // Road formation - use imported/entered value, don't auto-calculate
        const roadFormation = section.roadFormation || 0;
        
        const formationLevel = frl - roadTotal;
        const lessStrip = existingGroundLevel - stripValue;
        const depth = lessStrip - formationLevel; // Correct depth formula

        // Road area and capping area calculations
        const carriagewayCountForSelectedRoads = (data.cutFillSections || []).filter(
          (x: any) => x.carriagewayFootwayId === section.carriagewayFootwayId
        ).length;
        const roadArea = carriagewayCountForSelectedRoads > 0 
          ? roadFormation / carriagewayCountForSelectedRoads 
          : 0;
        const cappingArea = roadArea + (10 * 0.5 * 2);

        // Volume calculations
        const area = section.area || 0;
        let cutVolume = 0;
        let fillVolume = 0;
        if (depth > 0) {
          cutVolume = cappingArea * depth;
        } else if (depth < 0) {
          fillVolume = cappingArea * Math.abs(depth);
        }

        // Footpaths calculations
        let fpl = 0;
        if (roadType) {
          if (roadType.surface === "Tarmac Road") {
            fpl = frl + 0.125;
          } else if (roadType.surface === "Block Paved Road") {
            fpl = frl + 0.05;
          } else {
            fpl = frl + 0.125;
          }
        }

        // Path Form Level calculation
        const totalPath = carriagewayFootway ? carriagewayFootway.totalPath : 0;
        let pathFormLevel = 0;
        if (carriagewayFootway && totalPath > 0) {
          const tarmacPathSum = carriagewayFootway.tarmacPath || 0;
          const blockPavedPathSum = carriagewayFootway.blockPavedPathCrossover || 0;
          const crossoverSum =
            (carriagewayFootway.tarmacCrossover || 0) +
            (carriagewayFootway.heavyDutyCrossover || 0);
          const vergeSum = carriagewayFootway.verge || 0;

          const tarmacFootpathType = 1;
          const blockPavedFootpathType = 1;
          const xoverFootpathType = 1;
          const vergeFootpathType = 1;

          const calculation =
            (tarmacPathSum * tarmacFootpathType +
              blockPavedPathSum * blockPavedFootpathType +
              crossoverSum * xoverFootpathType +
              vergeSum * vergeFootpathType) /
            totalPath;

          pathFormLevel = fpl - totalBuildUpValue;
        }

        // Footpaths fields
        const footpathsLessStrip = existingGroundLevel - stripValue;
        const footpathsDepth = pathFormLevel - footpathsLessStrip;
        const numberOfChainages = data.numberOfChainages || 1;
        const pathArea = totalPath / numberOfChainages;

        let footpathsCutVolume = 0;
        let footpathsFillVolume = 0;
        if (footpathsDepth > 0) {
          footpathsCutVolume = pathArea * footpathsDepth;
        } else if (footpathsDepth < 0) {
          footpathsFillVolume = pathArea * Math.abs(footpathsDepth);
        }

        return {
          ...section,
          carriageway,
          footway,
          frl,
          topsoilStrip,
          existingGroundLevel,
          stripValue,
          formationLevel,
          lessStrip,
          depth,
          area,
          roadArea,
          cappingArea,
          roadFormation,
          roadConstructionThickness: roadTotal,
          cutVolume,
          fillVolume,
          fpl,
          pathFormLevel,
          footpathsLessStrip,
          footpathsDepth,
          footpathsCutVolume,
          footpathsFillVolume,
          pathArea,
        };
      }
    );
    */

    // `data` here is a flat, RoadsExcelData-shaped object (built from imported Excel data
    // merged with legacy overrides) — re-nest it into the current RoadsData table/config
    // shape before writing back to state, preserving any config not touched by this import.
    updateField("roads", {
      ...roadsData,
      roadTypes: { ...roadsData.roadTypes, table: recalculatedRoadTypes },
      cutFillSections: {
        ...roadsData.cutFillSections,
        table: recalculatedCutFillSections,
        config: {
          ...roadsData.cutFillSections.config,
          stripValueDefault: data.stripValueDefault ?? roadsData.cutFillSections.config.stripValueDefault,
          tarmacConstructionTypeId: data.tarmacConstructionTypeId ?? roadsData.cutFillSections.config.tarmacConstructionTypeId,
          blockPavedConstructionTypeId: data.blockPavedConstructionTypeId ?? roadsData.cutFillSections.config.blockPavedConstructionTypeId,
        },
      },
      carriagewayFootways: {
        ...roadsData.carriagewayFootways,
        table: recalculatedCarriagewayFootways,
        config: {
          ...roadsData.carriagewayFootways.config,
          kerbRaceWidth: data.kerbRaceWidth ?? roadsData.carriagewayFootways.config.kerbRaceWidth,
          totalBuildUpValue: recalculatedTotalBuildUpValue,
        },
      },
      capping: {
        ...roadsData.capping,
        config: {
          ...roadsData.capping.config,
          cappingLayer: data.cappingLayer ?? roadsData.capping.config.cappingLayer,
          cappingType: data.cappingType ?? roadsData.capping.config.cappingType,
        },
      },
      footpaths: {
        ...roadsData.footpaths,
        config: data.footpathsConfig ?? roadsData.footpaths.config,
      },
      manualQtys: data.manualQtys ?? roadsData.manualQtys,
    });
  };

  // Unified Excel Import
  const handleUnifiedImport = async (file: File) => {
    setImportLoading(true);
    try {
      // Log 'Carriageway Take Off' sheet data to console
      await logCarriagewayTakeoffSheet(file);
      await logFootpathsConfigData(file);
      await logCarriagewaySheetSurvey(file);
      await logCutFillAndFootpathsData(file);

      // Parse road types, footpaths config, carriageway/footways, cut & fill sections, and strip value
      const [
        { roadTypes: importedRoadTypes, cappingType: importedCappingType },
        importedFootpathsConfig,
        importedCarriagewayFootways,
        importedCutFillSections,
        importedStripValue,
        importedKerbRaceWidth,
        importedRoadConstructionTypes,
        importedManualQtyEntries,
        importedCappingLayers,
      ] = await Promise.all([
        parseCarriagewayTakeoffRoadTypes(file),
        parseFootpathsConfig(file),
        parseCarriagewayAndFootwaysData(file),
        parseCutFillSections(file),
        parseStripValue(file),
        parseKerbRaceWidth(file),
        parseRoadConstructionTypeSelections(file),
        parseCarriagewaySummaryManualQtys(file),
        parseCappingLayersFromExcel(file),
      ]);

      const result = await importRoadsFromExcel(file);

      if (result.warnings.length > 0) {
        console.warn("Import warnings:", result.warnings);
      }

      // Merge Carriageway Take Off road types into the result so validation passes
      if (importedRoadTypes.length > 0) {
        result.data.roadTypes = importedRoadTypes;
      }

      // Validate — skip road-type / carriageway checks if those sheets weren't present
      const validation = validateRoadsData(result.data);
      const blockingErrors = validation.errors.filter(
        (e) => !e.includes('road type') && !e.includes('carriageway')
      );
      if (blockingErrors.length > 0) {
        alert(`Import validation failed:\n${blockingErrors.join("\n")}`);
        return;
      }
      if (!validation.isValid) {
        console.warn("Non-blocking validation warnings:", validation.errors);
      }

      // Resolve Carriageway Summary manual-qty entries (Filling with recycled 6F2 to
      // footpaths / Hot bond coat between layers) against the just-imported roads —
      // match by road name first, fall back to column position (Road 1 = 1st road, etc.)
      const finalCarriagewayFootways = importedCarriagewayFootways.length > 0
        ? importedCarriagewayFootways
        : result.data.carriagewayFootways;
      const importedManualQtys: Record<string, number> = {};
      importedManualQtyEntries.forEach(({ key, roadIndex, roadHeaderName, quantity }) => {
        const byName = finalCarriagewayFootways.find(
          (r) => r.roadName.trim().toLowerCase() === roadHeaderName.toLowerCase(),
        );
        const road = byName ?? finalCarriagewayFootways[roadIndex];
        if (!road) return;
        importedManualQtys[`${key}-${road.id}`] = quantity;
      });
      // Merge in Capping Layers' own manual entries ("Add exc"/"Add 6F2 fill", keyed
      // by section thickness rather than road) — a separate object, not a road-keyed
      // one, so it's combined rather than overwriting the Carriageway Summary entries.
      Object.assign(importedManualQtys, importedCappingLayers.manualQtys);

      // Completely replace with imported data; Carriageway Take Off parsers take precedence
      const updatedRoadsData = {
        ...result.data,
        ...(importedRoadTypes.length > 0 ? { roadTypes: importedRoadTypes, cappingType: importedCappingType } : {}),
        ...(importedFootpathsConfig ? { footpathsConfig: importedFootpathsConfig } : {}),
        ...(importedCarriagewayFootways.length > 0 ? { carriagewayFootways: importedCarriagewayFootways } : {}),
        ...(importedCutFillSections.length > 0 ? { cutFillSections: importedCutFillSections } : {}),
        ...(importedStripValue !== null ? { stripValueDefault: importedStripValue } : {}),
        ...(importedKerbRaceWidth !== null ? { kerbRaceWidth: importedKerbRaceWidth } : {}),
        ...(importedRoadConstructionTypes.tarmacConstructionTypeId !== null ? { tarmacConstructionTypeId: importedRoadConstructionTypes.tarmacConstructionTypeId } : {}),
        ...(importedRoadConstructionTypes.blockPavedConstructionTypeId !== null ? { blockPavedConstructionTypeId: importedRoadConstructionTypes.blockPavedConstructionTypeId } : {}),
        ...(Object.keys(importedManualQtys).length > 0 ? { manualQtys: importedManualQtys } : {}),
        ...(importedCappingLayers.thicknesses.length > 0 ? {
          cappingLayer: { enabled: true, thicknesses: importedCappingLayers.thicknesses, t1ThicknessMm: importedCappingLayers.t1ThicknessMm },
        } : {}),
      };

      // `updatedRoadsData` is a flat, RoadsExcelData-shaped object — re-nest it into the
      // current RoadsData table/config shape before writing to state (recalculateAllFields,
      // below, will shortly overwrite this again with fully-recalculated values).
      updateField("roads", {
        ...roadsData,
        roadTypes: { ...roadsData.roadTypes, table: updatedRoadsData.roadTypes || [] },
        cutFillSections: {
          ...roadsData.cutFillSections,
          table: updatedRoadsData.cutFillSections || [],
          config: {
            ...roadsData.cutFillSections.config,
            stripValueDefault: updatedRoadsData.stripValueDefault ?? roadsData.cutFillSections.config.stripValueDefault,
            tarmacConstructionTypeId: updatedRoadsData.tarmacConstructionTypeId ?? roadsData.cutFillSections.config.tarmacConstructionTypeId,
            blockPavedConstructionTypeId: updatedRoadsData.blockPavedConstructionTypeId ?? roadsData.cutFillSections.config.blockPavedConstructionTypeId,
          },
        },
        carriagewayFootways: {
          ...roadsData.carriagewayFootways,
          table: updatedRoadsData.carriagewayFootways || [],
          config: {
            ...roadsData.carriagewayFootways.config,
            kerbRaceWidth: updatedRoadsData.kerbRaceWidth ?? roadsData.carriagewayFootways.config.kerbRaceWidth,
          },
        },
        capping: {
          ...roadsData.capping,
          config: {
            ...roadsData.capping.config,
            cappingLayer: updatedRoadsData.cappingLayer ?? roadsData.capping.config.cappingLayer,
            cappingType: updatedRoadsData.cappingType ?? roadsData.capping.config.cappingType,
          },
        },
        footpaths: {
          ...roadsData.footpaths,
          config: updatedRoadsData.footpathsConfig ?? roadsData.footpaths.config,
        },
        manualQtys: updatedRoadsData.manualQtys ?? roadsData.manualQtys,
      });

      // Force recalculation after import
      setTimeout(() => {
        recalculateAllFields(updatedRoadsData);
      }, 100);

      setImportModalVisible(false);
      alert("Roads data imported successfully!");
    } catch (error) {
      console.error("Import failed:", error);
      alert(`Import failed: ${error.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  const summary = useMemo(
    () => calculateRoadSummary(roadsData.cutFillSections.table || [], roadsData.roadTypes.table || []),
    [roadsData.cutFillSections.table, roadsData.roadTypes.table]
  );

  return (
    <MainLayout
      canProceed={
        (roadsData.roadTypes.table || []).length > 0 &&
        (roadsData.cutFillSections.table || []).length > 0
      }
      showFooter={false}
    >
      <Card
        className="roads-card shadow-sm"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Sticky header + tabs */}
        <div
          className="roads-sticky-header"
          style={{
            position: 'sticky',
            top: '144px',
            backgroundColor: '#fff',
            zIndex: 98,
            borderRadius: '8px 8px 0 0',
            isolation: 'isolate',
          }}
        >
          {/* Header with border */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Typography.Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Roads Take-off
                </Typography.Title>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Configure road types, carriageways, footways and cut & fill
                  sections
                </Typography.Text>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {activeTab === "carriageway-footways" && selectedCarriagewayFootwayKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => requestDelete(() => {
                      deleteCarriagewayFootwayRows(selectedCarriagewayFootwayKeys);
                      setSelectedCarriagewayFootwayKeys([]);
                    }, selectedCarriagewayFootwayKeys.length)}
                    style={{ marginTop: "16px", height: "32px", borderRadius: "6px", fontWeight: 500 }}
                  >
                    Delete Selected ({selectedCarriagewayFootwayKeys.length})
                  </Button>
                )}
                {activeTab === "carriageway-footways" && (
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={() => {
                      roadTypesConfigSnapshotRef.current = roadsData.roadTypes.table || [];
                      setRoadTypesConfigOpen(true);
                    }}
                    type="default"
                    className="configure-headers-btn"
                    style={{
                      marginTop: "16px",
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      borderColor: "#1890ff",
                      color: "#1890ff",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Configure
                  </Button>
                )}
                {activeTab === "kerbs-edgings" && selectedKerbsEdgingsKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => requestDelete(() => {
                      deleteCarriagewayFootwayRows(selectedKerbsEdgingsKeys);
                      setSelectedKerbsEdgingsKeys([]);
                    }, selectedKerbsEdgingsKeys.length)}
                    style={{ marginTop: "16px", height: "32px", borderRadius: "6px", fontWeight: 500 }}
                  >
                    Delete Selected ({selectedKerbsEdgingsKeys.length})
                  </Button>
                )}
                {activeTab === "kerbs-edgings" && (
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={() => {
                      kerbsEdgingsConfigSnapshotRef.current = roadsData.carriagewayFootways.config;
                      setKerbsEdgingsConfigOpen(true);
                    }}
                    type="default"
                    className="configure-headers-btn"
                    style={{
                      marginTop: "16px",
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      borderColor: "#1890ff",
                      color: "#1890ff",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Configure
                  </Button>
                )}
                {activeTab === "footways-takeoff" && selectedFootwaysTakeoffKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => requestDelete(() => {
                      deleteCarriagewayFootwayRows(selectedFootwaysTakeoffKeys);
                      setSelectedFootwaysTakeoffKeys([]);
                    }, selectedFootwaysTakeoffKeys.length)}
                    style={{ marginTop: "16px", height: "32px", borderRadius: "6px", fontWeight: 500 }}
                  >
                    Delete Selected ({selectedFootwaysTakeoffKeys.length})
                  </Button>
                )}
                {activeTab === "cut-fill-sections" && selectedCutFillKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => requestDelete(() => {
                      deleteCutFillSections(selectedCutFillKeys);
                      setSelectedCutFillKeys([]);
                    }, selectedCutFillKeys.length)}
                    style={{ marginTop: "16px", height: "32px", borderRadius: "6px", fontWeight: 500 }}
                  >
                    Delete Selected ({selectedCutFillKeys.length})
                  </Button>
                )}
                {activeTab === "cut-fill-sections" && (
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={() => {
                      cutFillConfigSnapshotRef.current = roadsData.cutFillSections.config;
                      setCutFillConfigOpen(true);
                    }}
                    type="default"
                    className="configure-headers-btn"
                    style={{
                      marginTop: "16px",
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      borderColor: "#1890ff",
                      color: "#1890ff",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Configure
                  </Button>
                )}
                {activeTab === "footways-config" && (
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={() => {
                      footpathsConfigSnapshotRef.current = resolvedFootpathsConfig;
                      setFootpathsConfigOpen(true);
                    }}
                    type="default"
                    className="configure-headers-btn"
                    style={{
                      marginTop: "16px",
                      height: "32px",
                      borderRadius: "6px",
                      fontWeight: 500,
                      borderColor: "#1890ff",
                      color: "#1890ff",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Configure
                  </Button>
                )}
                {showDevMode && (
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => setImportModalVisible(true)}
                    style={{
                      color: "#1890ff",
                      fontWeight: 500,
                    }}
                  >
                    Import Take-off
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Custom Tab Buttons */}
          <div className="roads-sticky-tabs px-6" style={{ paddingTop: '16px', paddingBottom: '0px', display: "flex", flexWrap: "wrap", gap: "4px", backgroundColor: '#fff' }}>
            <Button
              type={
                activeTab === "carriageway-footways" ? "primary" : "default"
              }
              className={activeTab === "carriageway-footways" ? "active-tab-button" : "inactive-tab-button"}
              onClick={() => handleTabChange("carriageway-footways")}
              style={{
                backgroundColor:
                  activeTab === "carriageway-footways" ? "#1890ff" : "#E1E6EF",
                borderColor:
                  activeTab === "carriageway-footways" ? "#1890ff" : "#E1E6EF",
                color:
                  activeTab === "carriageway-footways" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "carriageway-footways"
                    ? "none"
                    : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "carriageway-footways" ? 2 : 1,
              }}
            >
              Carriageway Take Off
            </Button>
            <Button
              type={activeTab === "kerbs-edgings" ? "primary" : "default"}
              className={activeTab === "kerbs-edgings" ? "active-tab-button" : "inactive-tab-button"}
              onClick={() => handleTabChange("kerbs-edgings")}
              style={{
                backgroundColor:
                  activeTab === "kerbs-edgings" ? "#1890ff" : "#E1E6EF",
                borderColor:
                  activeTab === "kerbs-edgings" ? "#1890ff" : "#E1E6EF",
                color: activeTab === "kerbs-edgings" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "kerbs-edgings"
                    ? "none"
                    : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "kerbs-edgings" ? 2 : 1,
              }}
            >
              Kerbs &amp; Edgings
            </Button>
            <Button
              type={activeTab === "footways-takeoff" ? "primary" : "default"}
              className={activeTab === "footways-takeoff" ? "active-tab-button" : "inactive-tab-button"}
              onClick={() => handleTabChange("footways-takeoff")}
              style={{
                backgroundColor:
                  activeTab === "footways-takeoff" ? "#1890ff" : "#E1E6EF",
                borderColor:
                  activeTab === "footways-takeoff" ? "#1890ff" : "#E1E6EF",
                color: activeTab === "footways-takeoff" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "footways-takeoff"
                    ? "none"
                    : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "footways-takeoff" ? 2 : 1,
              }}
            >
              Footways
            </Button>
            <Button
              type={activeTab === "cut-fill-sections" ? "primary" : "default"}
              className={activeTab === "cut-fill-sections" ? "active-tab-button" : "inactive-tab-button"}
              onClick={() => handleTabChange("cut-fill-sections")}
              style={{
                backgroundColor:
                  activeTab === "cut-fill-sections" ? "#1890ff" : "#E1E6EF",
                borderColor:
                  activeTab === "cut-fill-sections" ? "#1890ff" : "#E1E6EF",
                color: activeTab === "cut-fill-sections" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "cut-fill-sections"
                    ? "none"
                    : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "cut-fill-sections" ? 2 : 1,
              }}
            >
              Carriageway Cut &amp; Fill
            </Button>
            <Button
              type={activeTab === "footways-config" ? "primary" : "default"}
              className={activeTab === "footways-config" ? "active-tab-button" : "inactive-tab-button"}
              onClick={() => handleTabChange("footways-config")}
              style={{
                backgroundColor:
                  activeTab === "footways-config" ? "#1890ff" : "#E1E6EF",
                borderColor:
                  activeTab === "footways-config" ? "#1890ff" : "#E1E6EF",
                color: activeTab === "footways-config" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "footways-config"
                    ? "none"
                    : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "footways-config" ? 2 : 1,
              }}
            >
              Footpaths
            </Button>
            <Button
              type={activeTab === "capping-layer" ? "primary" : "default"}
              className={activeTab === "capping-layer" ? "active-tab-button" : "inactive-tab-button"}
              onClick={() => handleTabChange("capping-layer")}
              style={{
                backgroundColor:
                  activeTab === "capping-layer" ? "#1890ff" : "#E1E6EF",
                borderColor:
                  activeTab === "capping-layer" ? "#1890ff" : "#E1E6EF",
                color: activeTab === "capping-layer" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "capping-layer" ? "none" : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "capping-layer" ? 2 : 1,
              }}
            >
              Capping Layers
            </Button>
            <Button
              type={activeTab === "carriageway-summary" ? "primary" : "default"}
              className={activeTab === "carriageway-summary" ? "active-tab-button" : "inactive-tab-button"}
              onClick={() => handleTabChange("carriageway-summary")}
              style={{
                backgroundColor:
                  activeTab === "carriageway-summary" ? "#1890ff" : "#E1E6EF",
                borderColor:
                  activeTab === "carriageway-summary" ? "#1890ff" : "#E1E6EF",
                color: activeTab === "carriageway-summary" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "carriageway-summary" ? "none" : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "carriageway-summary" ? 2 : 1,
              }}
            >
              Carriageway Summary
            </Button>
            <div style={{ flex: 1 }} />
            {activeTab === "capping-layer" && (
              <ExpandCollapseAllButton
                expanded={cappingActiveKeys.length > 0}
                style={{ height: "32px", borderRadius: "6px", fontWeight: 500, alignSelf: "center" }}
                onToggle={() => setCappingActiveKeys(cappingActiveKeys.length > 0 ? [] : cappingThicknesses.map((_, idx) => String.fromCharCode(65 + idx)))}
              />
            )}
            {activeTab === "carriageway-summary" && (
              <ExpandCollapseAllButton
                expanded={carriagewaySummaryExpandedKeys.length > 0}
                style={{ height: "32px", borderRadius: "6px", fontWeight: 500, alignSelf: "center" }}
                onToggle={() => setCarriagewaySummaryExpandedKeys(carriagewaySummaryExpandedKeys.length > 0 ? [] : CARRIAGEWAY_SUMMARY_SECTION_KEYS)}
              />
            )}
          </div>
        </div>

        {/* Content section */}
        <div className="px-6 pb-6 roads-content-section" style={{ paddingTop: '0px', position: 'relative', zIndex: 1 }}>
          {/* Tabbed Content */}
          <div style={{ borderRadius: "0 0 6px 6px" }}>
            {activeTab === "carriageway-footways" && (
              <div style={{ backgroundColor: "#fff" }}>
                <>
                  <CarriagewayFootwaySection
                    carriagewayFootways={roadsData.carriagewayFootways.table || []}
                    roadTypes={mergedRoadTypes}
                    onUpdate={updateCarriagewayFootways}
                    selectedRowKeys={selectedCarriagewayFootwayKeys}
                    onSelectedRowKeysChange={setSelectedCarriagewayFootwayKeys}
                    stickyOffset={stickyOffset}
                    isViewMode={state.isViewMode}
                  />
                  <div style={{ marginTop: "16px" }}>
                    <Button
                      type="text"
                      icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                      onClick={addCarriagewayFootway}
                      style={{ color: COLORS.primary, fontWeight: 500, padding: "4px 8px" }}
                    >
                      Add Road
                    </Button>
                  </div>
                </>
              </div>
            )}
            {activeTab === "kerbs-edgings" && (
              <div style={{ backgroundColor: "#fff" }}>
                <>
                  <KerbsEdgingsSection
                    carriagewayFootways={roadsData.carriagewayFootways.table || []}
                    onUpdate={updateCarriagewayFootways}
                    selectedRowKeys={selectedKerbsEdgingsKeys}
                    onSelectedRowKeysChange={setSelectedKerbsEdgingsKeys}
                    stickyOffset={stickyOffset}
                    isViewMode={state.isViewMode}
                  />
                  <div style={{ marginTop: "16px" }}>
                    <Button
                      type="text"
                      icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                      onClick={addCarriagewayFootway}
                      style={{ color: COLORS.primary, fontWeight: 500, padding: "4px 8px" }}
                    >
                      Add Road
                    </Button>
                  </div>
                </>
              </div>
            )}
            {activeTab === "footways-takeoff" && (
              <div style={{ backgroundColor: "#fff" }}>
                <>
                  <FootwaysTakeoffSection
                    carriagewayFootways={roadsData.carriagewayFootways.table || []}
                    onUpdate={updateCarriagewayFootways}
                    selectedRowKeys={selectedFootwaysTakeoffKeys}
                    onSelectedRowKeysChange={setSelectedFootwaysTakeoffKeys}
                    stickyOffset={stickyOffset}
                    isViewMode={state.isViewMode}
                  />
                  <div style={{ marginTop: "16px" }}>
                    <Button
                      type="text"
                      icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                      onClick={addCarriagewayFootway}
                      style={{ color: COLORS.primary, fontWeight: 500, padding: "4px 8px" }}
                    >
                      Add Road
                    </Button>
                  </div>
                </>
              </div>
            )}
            {activeTab === "cut-fill-sections" && (
              <div style={{ backgroundColor: "#fff" }}>
                <style>{`
                  .carriageway-cutfill-table .ant-table-thead > tr > th {
                    background-color: ${COLORS.tableHeaderBg} !important;
                    font-weight: 600 !important;
                    color: #000 !important;
                    border-right: 1px solid #e5e7eb !important;
                    border-bottom: 1px solid #e5e7eb !important;
                    padding: 10px 6px 8px 6px !important;
                    text-align: center !important;
                    font-size: 12px !important;
                    vertical-align: top !important;
                  }
                  .carriageway-cutfill-table .ant-table-thead > tr > th .cf-header-title {
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                    white-space: nowrap !important;
                    display: block !important;
                    width: 100% !important;
                  }
                  .carriageway-cutfill-table .ant-table-thead > tr > th .cf-header-unit {
                    font-size: 11px !important;
                    color: #6b7280 !important;
                    font-weight: 500 !important;
                  }
                  .carriageway-cutfill-table .ant-table-thead > tr > th.ant-table-cell-fix-left,
                  .carriageway-cutfill-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
                    background-color: ${COLORS.tableHeaderBg} !important;
                    z-index: 100 !important;
                  }
                  .carriageway-cutfill-table .ant-table-tbody > tr > td.ant-table-cell-fix-left {
                    background-color: #fff !important;
                  }
                  .carriageway-cutfill-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
                    background-color: #fafafa !important;
                  }
                  .carriageway-cutfill-table .ant-table-tbody > tr.ant-table-row-selected > td {
                    background-color: #fff !important;
                  }
                  .carriageway-cutfill-table .ant-table-tbody > tr.ant-table-row-selected:hover > td {
                    background-color: #fafafa !important;
                  }
                  .cf-road-collapse > .ant-collapse-item > .ant-collapse-header {
                    background-color: ${COLORS.tableHeaderBg};
                    font-weight: 600;
                    position: sticky !important;
                    top: ${stickyOffset}px !important;
                    z-index: 99 !important;
                    border-bottom: 1px solid #d9d9d9;
                  }
                `}</style>

                {cutFillCalculatedRoadGroups.length === 0 ? (
                  <Alert
                    message="No roads yet. Add roads on the Carriageway Take-off tab first."
                    type="info"
                    showIcon
                    style={{ marginTop: "16px" }}
                  />
                ) : (
                  <>
                  <Collapse
                    className="cf-road-collapse"
                    accordion
                    activeKey={openCutFillRoads}
                    onChange={(keys) => setOpenCutFillRoads(Array.isArray(keys) ? keys : [keys])}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 4 }}
                  >
                    {cutFillCalculatedRoadGroups.map(({ roadId, roadName, sections }) => {
                      const distinctSections = dedupeSectionsByChainage(sections);
                      const totalCut = distinctSections.reduce((s, r) => s + (r.cutVolume || 0), 0);
                      const totalFill = -distinctSections.reduce((s, r) => s + (r.fillVolume || 0), 0);
                      const totalCapping = distinctSections.reduce((s, r) => s + (r.cappingArea || 0), 0);
                      const cf = (roadsData.carriagewayFootways.table || []).find((r) => r.id === roadId);
                      const totalRoadArea = cf?.totalRoadArea || 0;
                      const sumTarmac = (cf?.tarmacSpec1 || 0) + (cf?.tarmacSpec2 || 0) + (cf?.tarmacSpec3 || 0) + (cf?.tarmacSpec4 || 0);
                      const sumBlock = (cf?.blockPavedSpec1 || 0) + (cf?.blockPavedSpec2 || 0) + (cf?.blockPavedSpec3 || 0) + (cf?.blockPavedSpec4 || 0);
                      const cappingRatio = totalRoadArea > 0 ? totalCapping / totalRoadArea : 0;
                      const tarmacCapping = sumTarmac * cappingRatio;
                      const blockCapping = sumBlock * cappingRatio;
                      return (
                        <Collapse.Panel
                          key={roadId}
                          header={
                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                              <span style={{ fontWeight: 600 }}>{roadName}</span>
                              <div style={{ display: "flex", alignItems: "center", gap: 16, marginLeft: "auto", paddingRight: 8 }}>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Capping: <strong style={{ color: COLORS.primary }}>{roundNearest(totalCapping)} m²</strong>
                                </span>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Road Area: <strong style={{ color: COLORS.primary }}>{roundNearest(totalRoadArea)} m²</strong>
                                </span>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Tarmac Capping: <strong style={{ color: COLORS.primary }}>{roundNearest(tarmacCapping)} m²</strong>
                                </span>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Block Capping: <strong style={{ color: COLORS.primary }}>{roundNearest(blockCapping)} m²</strong>
                                </span>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Cut: <strong style={{ color: "#D46B08" }}>{roundNearest(totalCut)} m³</strong>
                                </span>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Fill: <strong style={{ color: "#389E0D" }}>{roundNearest(totalFill)} m³</strong>
                                </span>
                              </div>
                            </div>
                          }
                        >
                          {openCutFillRoads.includes(roadId) && (
                            <>
                              {isTableLoading ? (
                                <TableLoadingPlaceholder />
                              ) : (
                              <Table
                                className="carriageway-cutfill-table"
                                columns={cutFillColumns}
                                dataSource={sections}
                                rowKey="id"
                                rowSelection={state.isViewMode ? undefined : {
                                  selectedRowKeys: selectedCutFillKeys,
                                  onChange: setSelectedCutFillKeys,
                                }}
                                pagination={{
                                  pageSize: CUT_FILL_PAGE_SIZE,
                                  hideOnSinglePage: true,
                                  showSizeChanger: false,
                                  current: cutFillCurrentPage[roadId] || 1,
                                  onChange: (page) =>
                                    setCutFillCurrentPage((prev) => ({ ...prev, [roadId]: page })),
                                }}
                                size="small"
                                scroll={{ x: 1580 }}
                                sticky={{ offsetHeader: stickyOffset + cfPanelHeaderHeight, offsetScroll: 9999 }}
                                bordered
                              />
                              )}
                              <div
                                ref={(el) => {
                                  cutFillAddChainageBtnRefs.current[roadId] = el;
                                }}
                                style={{ padding: "8px 0" }}
                              >
                                <Button
                                  type="text"
                                  icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                                  onClick={() => addCutFillSection(roadId)}
                                  style={{ color: COLORS.primary, fontWeight: 500, padding: "4px 8px" }}
                                >
                                  Add Chainage
                                </Button>
                              </div>
                            </>
                          )}
                        </Collapse.Panel>
                      );
                    })}
                  </Collapse>
                  {(() => {
                    const grandTotalCut = cutFillCalculatedRoadGroups.reduce(
                      (s, { sections }) => s + dedupeSectionsByChainage(sections).reduce((s2, r) => s2 + (r.cutVolume || 0), 0),
                      0
                    );
                    const grandTotalFill = -cutFillCalculatedRoadGroups.reduce(
                      (s, { sections }) => s + dedupeSectionsByChainage(sections).reduce((s2, r) => s2 + (r.fillVolume || 0), 0),
                      0
                    );
                    const grandTotalCapping = cutFillCalculatedRoadGroups.reduce(
                      (s, { sections }) => s + dedupeSectionsByChainage(sections).reduce((s2, r) => s2 + (r.cappingArea || 0), 0),
                      0
                    );
                    const grandTotalRoadArea = cutFillCalculatedRoadGroups.reduce((s, { roadId }) => {
                      const cf = (roadsData.carriagewayFootways.table || []).find((r) => r.id === roadId);
                      return s + (cf?.totalRoadArea || 0);
                    }, 0);
                    const grandTotalTarmacCapping = cutFillCalculatedRoadGroups.reduce((s, { roadId, sections }) => {
                      const totalCapping = dedupeSectionsByChainage(sections).reduce((s2, r) => s2 + (r.cappingArea || 0), 0);
                      const cf = (roadsData.carriagewayFootways.table || []).find((r) => r.id === roadId);
                      const totalRoadArea = cf?.totalRoadArea || 0;
                      const sumTarmac = (cf?.tarmacSpec1 || 0) + (cf?.tarmacSpec2 || 0) + (cf?.tarmacSpec3 || 0) + (cf?.tarmacSpec4 || 0);
                      const cappingRatio = totalRoadArea > 0 ? totalCapping / totalRoadArea : 0;
                      return s + sumTarmac * cappingRatio;
                    }, 0);
                    const grandTotalBlockCapping = cutFillCalculatedRoadGroups.reduce((s, { roadId, sections }) => {
                      const totalCapping = dedupeSectionsByChainage(sections).reduce((s2, r) => s2 + (r.cappingArea || 0), 0);
                      const cf = (roadsData.carriagewayFootways.table || []).find((r) => r.id === roadId);
                      const totalRoadArea = cf?.totalRoadArea || 0;
                      const sumBlock = (cf?.blockPavedSpec1 || 0) + (cf?.blockPavedSpec2 || 0) + (cf?.blockPavedSpec3 || 0) + (cf?.blockPavedSpec4 || 0);
                      const cappingRatio = totalRoadArea > 0 ? totalCapping / totalRoadArea : 0;
                      return s + sumBlock * cappingRatio;
                    }, 0);
                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 16,
                          marginTop: 12,
                          padding: "10px 16px",
                          border: "1px solid #e5e7eb",
                          borderRadius: 4,
                          backgroundColor: COLORS.tableHeaderBg,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>Totals (all roads)</span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Capping: <strong style={{ color: COLORS.primary }}>{roundNearest(grandTotalCapping)} m²</strong>
                        </span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Road Area: <strong style={{ color: COLORS.primary }}>{roundNearest(grandTotalRoadArea)} m²</strong>
                        </span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Tarmac Capping: <strong style={{ color: COLORS.primary }}>{roundNearest(grandTotalTarmacCapping)} m²</strong>
                        </span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Block Capping: <strong style={{ color: COLORS.primary }}>{roundNearest(grandTotalBlockCapping)} m²</strong>
                        </span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Cut: <strong style={{ color: "#D46B08" }}>{roundNearest(grandTotalCut)} m³</strong>
                        </span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Fill: <strong style={{ color: "#389E0D" }}>{roundNearest(grandTotalFill)} m³</strong>
                        </span>
                      </div>
                    );
                  })()}
                  </>
                )}
              </div>
            )}
            {activeTab === "footways-config" && (
              <div style={{ backgroundColor: "#fff" }}>
                <style>{`
                  .footpaths-table .ant-table-thead > tr > th {
                    background-color: ${COLORS.tableHeaderBg} !important;
                    font-weight: 600 !important;
                    padding: 10px 6px 8px 6px !important;
                    text-align: center !important;
                    font-size: 12px !important;
                    vertical-align: top !important;
                  }
                  .footpaths-table .ant-table-thead > tr > th .cf-header-title {
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                    white-space: nowrap !important;
                    display: block !important;
                    width: 100% !important;
                  }
                  .footpaths-table .ant-table-thead > tr > th .cf-header-unit {
                    font-size: 11px !important;
                    color: #6b7280 !important;
                    font-weight: 500 !important;
                  }
                  .fp-road-collapse > .ant-collapse-item > .ant-collapse-header {
                    background-color: ${COLORS.tableHeaderBg};
                    font-weight: 600;
                    position: sticky !important;
                    top: ${stickyOffset}px !important;
                    z-index: 99 !important;
                    border-bottom: 1px solid #d9d9d9;
                  }
                `}</style>

                {footpathsCutFillRoadGroups.length === 0 ? (
                  <Alert
                    message="No footpath data. Add Carriageway Cut & Fill sections first — footpath values are calculated automatically."
                    type="info"
                    showIcon
                    style={{ marginTop: "16px" }}
                  />
                ) : (
                  <>
                  <Collapse
                    className="fp-road-collapse"
                    accordion
                    activeKey={openFootpathRoads}
                    onChange={(keys) => setOpenFootpathRoads(Array.isArray(keys) ? keys : [keys])}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 4 }}
                  >
                    {footpathsCutFillRoadGroups.map(({ roadId, roadName, sections }) => {
                      const totalCut = sections.reduce((s, r) => s + (r.footpathsCutVolume || 0), 0);
                      const totalFill = -sections.reduce((s, r) => s + (r.footpathsFillVolume || 0), 0);
                      const totalPath = sections.reduce((s, r) => s + (r.pathArea || 0), 0);
                      return (
                        <Collapse.Panel
                          key={roadId}
                          header={
                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                              <span style={{ fontWeight: 600 }}>{roadName}</span>
                              <div style={{ display: "flex", alignItems: "center", gap: 16, marginLeft: "auto", paddingRight: 8 }}>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Cut: <strong style={{ color: "#D46B08" }}>{roundNearest(totalCut)} m³</strong>
                                </span>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Fill: <strong style={{ color: "#389E0D" }}>{roundNearest(totalFill)} m³</strong>
                                </span>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Path: <strong style={{ color: COLORS.primary }}>{roundNearest(totalPath)} m²</strong>
                                </span>
                              </div>
                            </div>
                          }
                        >
                          {openFootpathRoads.includes(roadId) && (isTableLoading ? (
                            <TableLoadingPlaceholder />
                          ) : (
                            <Table
                              className="footpaths-table"
                              columns={footpathsColumns}
                              dataSource={sections}
                              rowKey="id"
                              pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
                              size="small"
                              scroll={{ x: 1000 }}
                              sticky={{ offsetHeader: stickyOffset + fpPanelHeaderHeight, offsetScroll: 9999 }}
                              bordered
                            />
                          ))}
                        </Collapse.Panel>
                      );
                    })}
                  </Collapse>
                  {(() => {
                    const grandTotalCut = footpathsCutFillRoadGroups.reduce(
                      (s, { sections }) => s + sections.reduce((s2, r) => s2 + (r.footpathsCutVolume || 0), 0),
                      0
                    );
                    const grandTotalFill = -footpathsCutFillRoadGroups.reduce(
                      (s, { sections }) => s + sections.reduce((s2, r) => s2 + (r.footpathsFillVolume || 0), 0),
                      0
                    );
                    const grandTotalPath = footpathsCutFillRoadGroups.reduce(
                      (s, { sections }) => s + sections.reduce((s2, r) => s2 + (r.pathArea || 0), 0),
                      0
                    );
                    return (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 16,
                          marginTop: 12,
                          padding: "10px 16px",
                          border: "1px solid #e5e7eb",
                          borderRadius: 4,
                          backgroundColor: COLORS.tableHeaderBg,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>Totals (all roads)</span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Cut: <strong style={{ color: "#D46B08" }}>{roundNearest(grandTotalCut)} m³</strong>
                        </span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Fill: <strong style={{ color: "#389E0D" }}>{roundNearest(grandTotalFill)} m³</strong>
                        </span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>
                          Path: <strong style={{ color: COLORS.primary }}>{roundNearest(grandTotalPath)} m²</strong>
                        </span>
                      </div>
                    );
                  })()}
                  </>
                )}
              </div>
            )}
            {activeTab === "carriageway-summary" && (
              <div style={{ backgroundColor: "#fff" }}>
                <CarriagewaySummary
                  showHeader={false}
                  noPadding
                  expandedRowKeys={carriagewaySummaryExpandedKeys}
                  onExpandedRowKeysChange={setCarriagewaySummaryExpandedKeys}
                  stickyOffset={stickyOffset}
                />
              </div>
            )}
            {activeTab === "capping-layer" && (() => {
              type CappingRowType = "section" | "item" | "blank" | "label";
              interface CappingRow {
                key: string;
                rowType: CappingRowType;
                description: string;
                unit?: string;
                qty?: number | null;
                formulaInfo?: CappingFormulaInfo;
                children?: CappingRow[];
                mm?: number;
                /** Item library resource code for this row, when known — used only to
                 * render the dev-mode-only "Evaluate Code" column + its looked-up rate. */
                evaluateCode?: string;
                /** When set, the Quant cell renders as an editable NumberInput bound to
                 * roadsData.manualQtys[manualKey] instead of a computed/blank value. */
                manualKey?: string;
              }

              // ── Aggregate source values for formulas ──────────────────────
              // roadArea/cappingArea/cutVolume/fillVolume are derived fields (see
              // computeCutFillFields) — not stored on roadsData.cutFillSections.table,
              // only computed live in cutFillCalculatedRoadGroups (same source the Cut &
              // Fill tab renders from). Reading the raw table here left these all at 0.
              const cutFills = cutFillCalculatedRoadGroups.flatMap((g) => g.sections);
              // Deduped per road group (not on the already-flattened `cutFills`) — chainage
              // numbers repeat across different roads, so deduping post-flatten would wrongly
              // collide rows from different roads that happen to share a chainage value.
              const dedupedCutFills = cutFillCalculatedRoadGroups.flatMap((g) => dedupeSectionsByChainage(g.sections));
              const cfws = roadsData.carriagewayFootways.table || [];

              const totalCutVolume = dedupedCutFills.reduce((s, c) => s + (c.cutVolume ?? 0), 0);
              const totalFillVolume = dedupedCutFills.reduce((s, c) => s + (c.fillVolume ?? 0), 0);
              const totalTarmacSubBase = cfws.reduce(
                (s, c) => s + (c.tarmacSpec1 ?? 0) + (c.tarmacSpec2 ?? 0) + (c.tarmacSpec3 ?? 0) + (c.tarmacSpec4 ?? 0), 0,
              );
              const totalBlockSubBase = cfws.reduce(
                (s, c) => s + (c.blockPavedSpec1 ?? 0) + (c.blockPavedSpec2 ?? 0) + (c.blockPavedSpec3 ?? 0) + (c.blockPavedSpec4 ?? 0), 0,
              );
              // Capping formation area = sum of cappingArea from cut/fill sections (Road Area + 10×0.5×2)
              const totalCappingArea = dedupedCutFills.reduce((s, c) => s + (c.cappingArea ?? 0), 0);
              // Stockpile = E11 - E6 = 0 - (-totalCutVolume) = totalCutVolume (Add exc is empty in Excel)
              const stockpileQty = totalCutVolume;

              // Capping material text is the Carriageway Takeoff's own capping dropdown
              // value verbatim ("R/C 6F2 capping" / "Q 6F5 capping") — no separate
              // default; unset shows "xxx".
              const cappingMaterial = roadsData.capping.config.cappingType ?? "xxx";

              // "Add exc" and "Add 6F2 fill" have no formula (unlike "Omit exc"/"Omit 6F2
              // fill" etc.) — they're the estimator's own manual entry, per capping
              // thickness section, same manualQtys store/pattern RoadsBillSummary.tsx
              // uses for its own manual rows (e.g. cycleway regulating).
              const cappingManualQtys = roadsData.manualQtys ?? {};
              const setCappingManualQty = (key: string, value: number) => {
                updateField("roads", { ...roadsData, manualQtys: { ...cappingManualQtys, [key]: value } });
              };

              const getCappingChildren = (mm: number, prefix: string): CappingRow[] => [
                {
                  key: `${prefix}-omit-exc`,
                  rowType: "item",
                  description: "Omit exc",
                  unit: "m³",
                  qty: totalCutVolume > 0 ? -totalCutVolume : null,
                  evaluateCode: findCappingLayersEvaluateCode("Omit exc"),
                  formulaInfo: {
                    label: "Omit exc",
                    formula: "−Σ(cutVolume) across all cut/fill sections",
                    excelRef: "−BF1464",
                    rows: cutFills
                      .filter((c) => (c.cutVolume ?? 0) > 0)
                      .map((c) => ({ ref: c.chainage || c.id || "Section", value: -(c.cutVolume ?? 0), unit: "m³" })),
                    total: -totalCutVolume,
                    unit: "m³",
                  },
                },
                {
                  key: `${prefix}-omit-6f2`,
                  rowType: "item",
                  description: "Omit 6F2 fill",
                  unit: "m³",
                  qty: totalFillVolume > 0 ? -totalFillVolume : null,
                  evaluateCode: findCappingLayersEvaluateCode("Omit 6F2 fill"),
                  formulaInfo: {
                    label: "Omit 6F2 fill",
                    formula: "−Σ(fillVolume) across all cut/fill sections, across all roads",
                    excelRef: "BG1464",
                    rows: cutFills
                      .filter((c) => (c.fillVolume ?? 0) > 0)
                      .map((c) => ({ ref: c.chainage || c.id || "Section", value: -(c.fillVolume ?? 0), unit: "m³" })),
                    total: -totalFillVolume,
                    unit: "m³",
                  },
                },
                {
                  key: `${prefix}-omit-t1t`,
                  rowType: "item",
                  description: t1ThicknessMm[`${mm}-tarmac`]
                    ? `Omit ${t1ThicknessMm[`${mm}-tarmac`]}mm thick Type 1 to tarmac road`
                    : "Omit xxmm thick Type 1 to tarmac road",
                  unit: "m²",
                  qty: totalTarmacSubBase > 0 ? -totalTarmacSubBase : null,
                  evaluateCode: findCappingLayersEvaluateCode(
                    t1ThicknessMm[`${mm}-tarmac`]
                      ? `Omit ${t1ThicknessMm[`${mm}-tarmac`]}mm thick Type 1 to tarmac road`
                      : "Omit xxmm thick Type 1 to tarmac road",
                  ),
                  formulaInfo: {
                    label: "Omit Type 1 to tarmac road",
                    formula: "−Σ(tarmacSpec1+tarmacSpec2+tarmacSpec3+tarmacSpec4) across all carriageway/footway records",
                    excelRef: "−F1468",
                    rows: cfws
                      .filter((c) => ((c.tarmacSpec1 ?? 0) + (c.tarmacSpec2 ?? 0) + (c.tarmacSpec3 ?? 0) + (c.tarmacSpec4 ?? 0)) > 0)
                      .map((c) => ({
                        ref: c.roadName || c.id || "Road",
                        value: -(((c.tarmacSpec1 ?? 0) + (c.tarmacSpec2 ?? 0) + (c.tarmacSpec3 ?? 0) + (c.tarmacSpec4 ?? 0))),
                        unit: "m²",
                      })),
                    total: -totalTarmacSubBase,
                    unit: "m²",
                  },
                },
                {
                  key: `${prefix}-omit-t1b`,
                  rowType: "item",
                  description: t1ThicknessMm[`${mm}-block`]
                    ? `Omit ${t1ThicknessMm[`${mm}-block`]}mm thick Type 1 to block paved road`
                    : "Omit xxmm thick Type 1 to block paved road",
                  unit: "m²",
                  qty: totalBlockSubBase > 0 ? -totalBlockSubBase : null,
                  evaluateCode: findCappingLayersEvaluateCode(
                    t1ThicknessMm[`${mm}-block`]
                      ? `Omit ${t1ThicknessMm[`${mm}-block`]}mm thick Type 1 to block paved road`
                      : "Omit xxmm thick Type 1 to block paved road",
                  ),
                  formulaInfo: {
                    label: "Omit Type 1 to block paved road",
                    formula: "−Σ(blockPavedSpec1+blockPavedSpec2+blockPavedSpec3+blockPavedSpec4) across all carriageway/footway records",
                    excelRef: "−J1468",
                    rows: cfws
                      .filter((c) => ((c.blockPavedSpec1 ?? 0) + (c.blockPavedSpec2 ?? 0) + (c.blockPavedSpec3 ?? 0) + (c.blockPavedSpec4 ?? 0)) > 0)
                      .map((c) => ({
                        ref: c.roadName || c.id || "Road",
                        value: -(((c.blockPavedSpec1 ?? 0) + (c.blockPavedSpec2 ?? 0) + (c.blockPavedSpec3 ?? 0) + (c.blockPavedSpec4 ?? 0))),
                        unit: "m²",
                      })),
                    total: -totalBlockSubBase,
                    unit: "m²",
                  },
                },
                {
                  key: `${prefix}-add-exc`,
                  rowType: "item",
                  description: "Add exc",
                  unit: "m³",
                  // Manual entry, keyed by capping thickness (mm) rather than the
                  // section's letter prefix — letters shift if sections are added/
                  // removed/reordered, mm doesn't (capping thicknesses are unique).
                  qty: cappingManualQtys[`capping-${mm}-add-exc`] ?? null,
                  manualKey: `capping-${mm}-add-exc`,
                  evaluateCode: findCappingLayersEvaluateCode("Add exc"),
                },
                {
                  key: `${prefix}-add-6f2`,
                  rowType: "item",
                  description: "Add 6F2 fill",
                  unit: "m³",
                  // Manual entry, keyed by capping thickness (mm) rather than the
                  // section's letter prefix — letters shift if sections are added/
                  // removed/reordered, mm doesn't (capping thicknesses are unique).
                  qty: cappingManualQtys[`capping-${mm}-add-6f2`] ?? null,
                  manualKey: `capping-${mm}-add-6f2`,
                  evaluateCode: findCappingLayersEvaluateCode("Add 6F2 fill"),
                },
                {
                  key: `${prefix}-add-cap`,
                  rowType: "item",
                  description: `Add ${mm}mm ${cappingMaterial}`,
                  unit: "m²",
                  qty: totalCappingArea > 0 ? totalCappingArea : null,
                  evaluateCode: findCappingMaterialEvaluateCode(cappingMaterial),
                  formulaInfo: {
                    label: `Add ${mm}mm ${cappingMaterial}`,
                    formula: "Σ(cappingArea) across all Carriageway Cut & Fill sections — Road Area + (10 × 0.5 × 2)",
                    excelRef: "AW1467 = SUM(AW1465:AW1466)",
                    rows: cutFills
                      .filter((c) => (c.cappingArea ?? 0) > 0)
                      .map((c) => ({ ref: c.chainage || c.id || "Section", value: c.cappingArea ?? 0, unit: "m²" })),
                    total: totalCappingArea,
                    unit: "m²",
                  },
                },
                {
                  key: `${prefix}-add-stock`,
                  rowType: "item",
                  description: "Stockpile surplus onsite",
                  unit: "m³",
                  qty: stockpileQty > 0 ? stockpileQty : null,
                  evaluateCode: findCappingLayersEvaluateCode("Stockpile surplus onsite"),
                  formulaInfo: {
                    label: "Stockpile surplus onsite",
                    formula: "E11 − E6 = Add exc − Omit exc = 0 − (−Σ cutVolume) = Σ cutVolume",
                    excelRef: "E14 = E11 − E6",
                    rows: cutFills
                      .filter((c) => (c.cutVolume ?? 0) > 0)
                      .map((c) => ({ ref: c.chainage || c.id || "Section", value: c.cutVolume ?? 0, unit: "m³" })),
                    total: stockpileQty,
                    unit: "m³",
                  },
                },
                { key: `${prefix}-label`, rowType: "label", description: `${mm}mm CAPPING` },
                {
                  key: `${prefix}-eo`,
                  rowType: "item",
                  description: "EO remove surplus arisings from site",
                  unit: "m³",
                  qty: stockpileQty > 0 ? stockpileQty : null,
                  evaluateCode: findCappingLayersEvaluateCode("EO remove surplus arisings from site"),
                  formulaInfo: {
                    label: "EO remove surplus arisings from site",
                    formula: "= Stockpile surplus onsite (E14)",
                    excelRef: "E18 = E14",
                    rows: cutFills
                      .filter((c) => (c.cutVolume ?? 0) > 0)
                      .map((c) => ({ ref: c.chainage || c.id || "Section", value: c.cutVolume ?? 0, unit: "m³" })),
                    total: stockpileQty,
                    unit: "m³",
                  },
                },
              ];

              const cappingData: CappingRow[] = cappingThicknesses.map((mm, idx) => {
                const letter = String.fromCharCode(65 + idx);
                return {
                  key: letter,
                  rowType: "section" as const,
                  description: `${letter}. ${mm}mm CAPPING`,
                  children: getCappingChildren(mm, letter),
                  mm,
                };
              });

              const fmtCappingQty = (n: number) =>
                n.toLocaleString("en-GB", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

              // Per-road split of each site-wide capping quantity — road's share of the
              // total Carriageway Cut & Fill road area (roadArea/totalRoadArea), same
              // roads/order as Carriageway Summary (roadsData.carriagewayFootways.table).
              const cappingRoads = cfws;
              const roadAreaByRoadId: Record<string, number> = {};
              cfws.forEach((road) => {
                roadAreaByRoadId[road.id] = road.totalRoadArea || 0;
              });
              const totalRoadAreaAll = cfws.reduce((s, road) => s + (road.totalRoadArea || 0), 0);
              const roadPctById: Record<string, number> = {};
              cappingRoads.forEach((road) => {
                roadPctById[road.id] = totalRoadAreaAll > 0 ? (roadAreaByRoadId[road.id] ?? 0) / totalRoadAreaAll : 0;
              });

              const CAPPING_DESC_WIDTH = 300;
              const CAPPING_UNIT_WIDTH = 70;
              const CAPPING_ROAD_COL_WIDTH = 110;
              const CAPPING_TOTAL_WIDTH = 110;
              const CAPPING_ACTION_WIDTH = 90;
              const CAPPING_EVAL_CODE_WIDTH = 140;
              const cappingScrollX = CAPPING_DESC_WIDTH + CAPPING_UNIT_WIDTH
                + CAPPING_ROAD_COL_WIDTH * cappingRoads.length + CAPPING_TOTAL_WIDTH + CAPPING_ACTION_WIDTH
                + (showDevMode ? CAPPING_EVAL_CODE_WIDTH : 0);

              const cappingRoadCols = cappingRoads.map((road, idx) => ({
                title: (
                  <div style={{ textAlign: "right" as const }}>
                    <div>{road.roadName || `Road ${idx + 1}`}</div>
                    <div style={{ fontWeight: 400, fontSize: 11, color: "#6b7280" }}>
                      {(roadPctById[road.id] * 100).toFixed(0)}%
                    </div>
                  </div>
                ),
                key: `capping-road-${road.id}`,
                width: CAPPING_ROAD_COL_WIDTH,
                align: "right" as const,
                render: (_: unknown, record: CappingRow) => {
                  if (record.rowType === "label" || record.rowType === "blank") return "";
                  if (record.qty == null) return <Typography.Text type="secondary">—</Typography.Text>;
                  const val = record.qty * (roadPctById[road.id] ?? 0);
                  return (
                    <Typography.Text style={{ color: val < 0 ? "#cf1322" : undefined }}>
                      {fmtCappingQty(val)}
                    </Typography.Text>
                  );
                },
              }));

              const cappingCols = [
                {
                  title: "Description",
                  dataIndex: "description",
                  key: "description",
                  width: CAPPING_DESC_WIDTH,
                  onHeaderCell: () => ({ className: "capping-sticky-desc" }),
                  onCell: () => ({ className: "capping-sticky-desc" }),
                  render: (text: string, record: CappingRow) => {
                    if (record.rowType === "label")
                      return <Typography.Text strong className="text-indigo-600">{text}</Typography.Text>;
                    if (record.rowType === "blank") return null;
                    return (
                      <span>
                        <Typography.Text>{text}</Typography.Text>
                        {showDevMode && record.formulaInfo && (
                          <InfoCircleOutlined
                            style={{ color: "#1677ff", marginLeft: 6, cursor: "pointer" }}
                            onClick={(e) => { e.stopPropagation(); setCappingActiveFormula(record.formulaInfo ?? null); }}
                          />
                        )}
                      </span>
                    );
                  },
                },
                {
                  title: "Quant",
                  dataIndex: "qty",
                  key: "qty",
                  width: CAPPING_TOTAL_WIDTH,
                  align: "right" as const,
                  onHeaderCell: () => ({ className: "capping-sticky-quant" }),
                  onCell: () => ({ className: "capping-sticky-quant" }),
                  render: (_: unknown, record: CappingRow) => {
                    if (record.rowType === "label" || record.rowType === "blank") return "";
                    if (record.manualKey) {
                      const key = record.manualKey;
                      return (
                        <NumberInput
                          value={record.qty ?? 0}
                          onChange={(v) => setCappingManualQty(key, v ?? 0)}
                          decimalPlaces={3}
                          positiveOnly
                          size="small"
                          style={{ width: "100%", textAlign: "right" }}
                          disabled={state.isViewMode}
                          onClick={(e) => e.stopPropagation()}
                        />
                      );
                    }
                    if (record.qty == null) return <Typography.Text type="secondary">—</Typography.Text>;
                    return (
                      <Typography.Text strong style={{ color: record.qty < 0 ? "#cf1322" : undefined }}>
                        {fmtCappingQty(record.qty)}
                      </Typography.Text>
                    );
                  },
                },
                {
                  title: "Unit",
                  dataIndex: "unit",
                  key: "unit",
                  width: CAPPING_UNIT_WIDTH,
                  align: "center" as const,
                  onHeaderCell: () => ({ className: "capping-sticky-unit" }),
                  onCell: () => ({ className: "capping-sticky-unit" }),
                  render: (unit: string | undefined, record: CappingRow) => {
                    if (record.rowType === "label" || record.rowType === "blank") return "";
                    return formatUnit(unit);
                  },
                },
                ...(showDevMode ? [{
                  title: "Evaluate Code",
                  key: "evaluateCode",
                  width: CAPPING_EVAL_CODE_WIDTH,
                  onHeaderCell: () => ({ className: "capping-sticky-evalcode" }),
                  onCell: () => ({ className: "capping-sticky-evalcode" }),
                  render: (_: unknown, record: CappingRow) => {
                    if (record.rowType !== "item") return "";
                    if (!record.evaluateCode) return <Typography.Text type="secondary">—</Typography.Text>;
                    const libRate = getLibRate(record.evaluateCode);
                    return (
                      <span>
                        <Tag color="blue">{record.evaluateCode}</Tag>
                        {libRate !== undefined && (
                          <div style={{ color: "#52c41a", fontSize: 12, marginTop: 2 }}>
                            {formatCurrency(libRate)}
                          </div>
                        )}
                      </span>
                    );
                  },
                }] : []),
                ...cappingRoadCols,
                {
                  title: "Action",
                  key: "action",
                  width: CAPPING_ACTION_WIDTH,
                  align: "center" as const,
                  onHeaderCell: () => ({ className: "capping-sticky-action" }),
                  onCell: () => ({ className: "capping-sticky-action" }),
                  render: () => null,
                },
              ];

              return (
                <div>
                  {/* ONE shared horizontal scrollbar for the whole Capping Layers view —
                      capping-scroll-outer wraps the header AND every accordion panel's body
                      table together (one continuous scroll region, one scrollbar, at the
                      bottom of whichever table is tallest/visible). Each panel's own title
                      bar ("150mm CAPPING" + gear/delete buttons) is pinned with
                      position:sticky; left:0 (same mechanism as the Description/Quant/Unit/
                      Action columns below) so it doesn't move as this shared region scrolls —
                      its width is set explicitly to cappingViewportWidth (measured off this
                      wrapper's own clientWidth) rather than "100%", since "100%" here would
                      mean 100% of the wide scrolled content, not the visible viewport, and
                      push its right-aligned buttons off-screen. */}
                  <div className="capping-scroll-outer" ref={cappingScrollOuterRef} style={{ overflowX: "auto" }}>
                    <div style={{ minWidth: cappingScrollX }}>
                      {/* Shared column header — every panel's table reuses the same columns,
                          so render the header once instead of repeating it per panel. */}
                      <div className="capping-shared-header-wrapper">
                        <Table<CappingRow>
                          className="capping-shared-header"
                          bordered
                          size="middle"
                          showHeader
                          pagination={false}
                          rowKey="key"
                          dataSource={[]}
                          columns={cappingCols}
                          locale={{ emptyText: "" }}
                        />
                      </div>
                      <Collapse
                        activeKey={cappingActiveKeys}
                        onChange={(keys) => setCappingActiveKeys(Array.isArray(keys) ? keys as string[] : keys ? [keys as string] : [])}
                        className="capping-collapse"
                        style={{ '--capping-vw': cappingViewportWidth ? `${cappingViewportWidth}px` : '100%' } as React.CSSProperties}
                        size="large"
                        bordered={false}
                        expandIconPosition="start"
                        expandIcon={({ isActive }) => (
                          <span className={`capping-expand-icon${isActive ? "" : " capping-expand-icon-collapsed"}`} />
                        )}
                      >
                        {cappingData.map((section) => (
                          <Collapse.Panel
                            key={section.key}
                            header={
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Typography.Text strong className="text-indigo-600">{section.description}</Typography.Text>
                                <span onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    type="text"
                                    size="small"
                                    className="configure-headers-btn"
                                    icon={<SettingOutlined style={{ color: COLORS.primary }} />}
                                    onClick={() => {
                                      if (section.mm != null) openCappingSectionConfig(section.mm);
                                    }}
                                  />
                                  <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                      if (section.mm != null) requestDelete(() => deleteCappingSection(section.mm as number));
                                    }}
                                  />
                                </span>
                              </div>
                            }
                          >
                            <Table<CappingRow>
                              className="capping-table"
                              bordered
                              size="middle"
                              showHeader={false}
                              pagination={false}
                              rowKey="key"
                              dataSource={section.children}
                              columns={cappingCols}
                              rowClassName={(record) => {
                                if (record.rowType === "label") return "bg-indigo-50 font-semibold";
                                if (record.rowType === "blank") return "capping-blank-row";
                                return "";
                              }}
                            />
                          </Collapse.Panel>
                        ))}
                      </Collapse>
                    </div>
                  </div>
                  <style>{`
                    .capping-blank-row td {
                      padding: 3px 8px !important;
                      border-top-color: transparent !important;
                      border-bottom-color: transparent !important;
                    }
                    .capping-table .ant-table-thead > tr > th {
                      background-color: ${COLORS.tableHeaderBg} !important;
                    }
                    .capping-collapse { background: transparent; }
                    .capping-collapse > .ant-collapse-item {
                      margin-bottom: 0;
                      background: #fff;
                      border: 1px solid #e5e7eb !important;
                      border-top: none !important;
                      border-radius: 0 !important;
                      transition: box-shadow 0.15s ease, border-color 0.15s ease;
                    }
                    .capping-collapse > .ant-collapse-item:first-child {
                      border-top: 1px solid #e5e7eb !important;
                    }
                    .capping-collapse > .ant-collapse-item:hover {
                      border-color: #c7d2fe !important;
                    }
                    .capping-collapse .ant-collapse-content-box { padding: 0 !important; }
                    .capping-collapse .ant-collapse-content {
                      border-top: 1px solid #e5e7eb !important;
                      border-radius: 0;
                    }
                    /* Sticky as ONE unit (icon + title text + gear/delete buttons together) —
                       antd renders the expand-icon arrow as a sibling of the header prop's
                       own content, both children of .ant-collapse-header, so making only the
                       inner content sticky left the icon behind (out of sync) as the shared
                       capping-scroll-outer scrolled. Sticky-left only pins the left edge, not
                       the width, so width comes from --capping-vw (set on .capping-collapse,
                       measured off capping-scroll-outer's own clientWidth) rather than the
                       header's natural 100%-of-scrolled-content width. */
                    .capping-collapse .ant-collapse-header {
                      align-items: center !important;
                      padding: 14px 20px !important;
                      border-radius: 0 !important;
                      background-color: #eef2ff !important;
                      position: sticky !important;
                      left: 0 !important;
                      width: var(--capping-vw, 100%) !important;
                      box-sizing: border-box !important;
                      z-index: 2;
                    }
                    .capping-collapse .ant-collapse-item-active > .ant-collapse-header {
                      border-bottom: 1px solid #e0e7ff !important;
                    }
                    .capping-expand-icon {
                      position: relative;
                      display: inline-block;
                      width: 17px;
                      height: 17px;
                      background: #fff;
                      border: 1px solid #d9d9d9;
                      border-radius: 50%;
                      color: rgba(0, 0, 0, 0.88);
                      cursor: pointer;
                      transform: scale(0.94);
                    }
                    .capping-expand-icon::before,
                    .capping-expand-icon::after {
                      content: '';
                      position: absolute;
                      background: currentcolor;
                      transition: transform 0.2s ease-out;
                    }
                    .capping-expand-icon::before {
                      top: 7px;
                      left: 1px;
                      right: 1px;
                      height: 1px;
                    }
                    .capping-expand-icon::after {
                      top: 1px;
                      bottom: 1px;
                      left: 7px;
                      width: 1px;
                      transform: rotate(90deg);
                    }
                    .capping-expand-icon-collapsed::before {
                      transform: rotate(-180deg);
                    }
                    .capping-expand-icon-collapsed::after {
                      transform: rotate(0deg);
                    }
                    .capping-shared-header-wrapper { background-color: #fff; }
                    .capping-shared-header .ant-table-tbody,
                    .capping-shared-header .ant-table-placeholder {
                      display: none !important;
                    }
                    .capping-shared-header .ant-table {
                      border-radius: 0;
                    }
                    .capping-shared-header .ant-table-thead > tr > th {
                      background-color: ${COLORS.tableHeaderBg} !important;
                    }
                    /* Sticky columns (manual, since antd's "fixed" needs a table-owned
                       scroll.x — not used here, capping-scroll-outer is the one shared
                       scroll container for the header + every panel's body table). */
                    .capping-sticky-desc, .capping-sticky-quant, .capping-sticky-unit, .capping-sticky-evalcode, .capping-sticky-action {
                      position: sticky !important;
                      z-index: 2;
                      background-color: #fff;
                    }
                    .capping-shared-header .capping-sticky-desc,
                    .capping-shared-header .capping-sticky-quant,
                    .capping-shared-header .capping-sticky-unit,
                    .capping-shared-header .capping-sticky-evalcode,
                    .capping-shared-header .capping-sticky-action {
                      background-color: ${COLORS.tableHeaderBg} !important;
                      z-index: 3;
                    }
                    .capping-table tr.bg-indigo-50 .capping-sticky-desc,
                    .capping-table tr.bg-indigo-50 .capping-sticky-quant,
                    .capping-table tr.bg-indigo-50 .capping-sticky-unit,
                    .capping-table tr.bg-indigo-50 .capping-sticky-evalcode,
                    .capping-table tr.bg-indigo-50 .capping-sticky-action {
                      background-color: #eef2ff !important;
                    }
                    .capping-sticky-desc { left: 0; }
                    .capping-sticky-quant { left: ${CAPPING_DESC_WIDTH}px; }
                    .capping-sticky-unit { left: ${CAPPING_DESC_WIDTH + CAPPING_TOTAL_WIDTH}px; }
                    .capping-sticky-evalcode { left: ${CAPPING_DESC_WIDTH + CAPPING_TOTAL_WIDTH + CAPPING_UNIT_WIDTH}px; }
                    .capping-sticky-action { right: 0; }
                  `}</style>

                  {/* Add New Capping Layer section — same pattern as POS.tsx's "Add New POS" */}
                  <div style={{ marginTop: "16px" }}>
                    <Button
                      type="text"
                      icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                      onClick={openAddCappingSection}
                      style={{ color: COLORS.primary, fontWeight: 500, padding: "4px 8px" }}
                    >
                      Add New Capping Layer
                    </Button>
                  </div>

                  {/* Formula info modal — matches Carriageway Summary/Foul Sewers' column-calculation-info modal */}
                  {cappingActiveFormula && (
                    <Modal
                      title={`${cappingActiveFormula.label} — Calculation`}
                      open={true}
                      onCancel={() => setCappingActiveFormula(null)}
                      footer={[<Button key="close" onClick={() => setCappingActiveFormula(null)}>Close</Button>]}
                      width={500}
                    >
                      <div style={{ marginBottom: 12, padding: "8px 12px", background: "#f0f4ff", borderRadius: 4, fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                        {cappingActiveFormula.formula}
                      </div>
                    </Modal>
                  )}

                  {/* Add Capping Layer section — asks for the thickness (mm), names the section "{mm}mm CAPPING" */}
                  <Modal
                    title="Add Capping Layer Section"
                    open={addCappingModalVisible}
                    onCancel={() => setAddCappingModalVisible(false)}
                    onOk={confirmAddCappingSection}
                    okText="Add"
                    okButtonProps={{ disabled: !!newCappingThicknessMm && cappingThicknesses.includes(newCappingThicknessMm) }}
                  >
                    <div style={{ marginBottom: 8 }}>Capping thickness (mm)</div>
                    <NumberInput
                      value={newCappingThicknessMm ?? 0}
                      onChange={(v) => setNewCappingThicknessMm(v)}
                      decimalPlaces={0}
                      isInteger
                      style={{ width: "100%" }}
                      placeholder="e.g. 250"
                    />
                    {!!newCappingThicknessMm && cappingThicknesses.includes(newCappingThicknessMm) && (
                      <Alert
                        style={{ marginTop: 12 }}
                        type="warning"
                        showIcon
                        message={`${newCappingThicknessMm}mm CAPPING already exists`}
                      />
                    )}
                  </Modal>

                  {/* Capping section Configure modal — same per-section gear pattern as POS.tsx */}
                  <ColumnConfigModal
                    title={cappingConfigMm !== null ? `Configure ${cappingConfigMm}mm CAPPING` : "Configure"}
                    open={cappingConfigModalVisible}
                    onClose={() => setCappingConfigModalVisible(false)}
                    onApply={applyCappingSectionConfig}
                    width={450}
                    isViewMode={state.isViewMode}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      <ConfigField label="Type 1 to tarmac road, xxmm thick">
                        <NumberInput
                          value={cappingConfigDraft.tarmac}
                          onChange={(v) => setCappingConfigDraft((prev) => ({ ...prev, tarmac: v ?? 0 }))}
                          decimalPlaces={3}
                          style={{ width: "100%" }}
                        />
                      </ConfigField>
                      <ConfigField label="Type 1 to block paved road, xxmm thick">
                        <NumberInput
                          value={cappingConfigDraft.block}
                          onChange={(v) => setCappingConfigDraft((prev) => ({ ...prev, block: v ?? 0 }))}
                          decimalPlaces={3}
                          style={{ width: "100%" }}
                        />
                      </ConfigField>
                    </div>
                  </ColumnConfigModal>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Footer with Save & Continue button */}
        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "16px" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      {/* View Rates Modal */}
      <Modal
        title="Roads - View Rates"
        open={isViewRatesModalOpen}
        onCancel={() => setIsViewRatesModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewRatesModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        <div>
          {/* Site Clearance & Earthworks Section */}
          <div className="mb-6">
            <Typography.Title level={4} className="mb-4 text-blue-900">
              {roadRates.siteClearanceEarthworks[0].category}
            </Typography.Title>

            {(() => {
              const rates = calculateAllRoadRates();
              const tableData = roadRates.siteClearanceEarthworks[0].items.map(
                (item, index) => {
                  // Calculate values for specific items
                  if (item.description === "Site strip to remove vegetation") {
                    return {
                      key: index + 1,
                      description: item.description,
                      quantity: rates.totalArea,
                      unit: item.unit,
                      rate: item.rate,
                      amount: rates.siteStripAmount,
                    };
                  } else if (
                    item.description ===
                    "Excavate to reduce levels to take up 296mm top soil, deposit onsite"
                  ) {
                    return {
                      key: index + 1,
                      description: item.description,
                      quantity: rates.siteStripAmount * rates.stripValue,
                      unit: item.unit,
                      rate: item.rate,
                      amount: rates.excavateAmount,
                    };
                  }
                  // Default for other items
                  return {
                    key: index + 1,
                    description: item.description,
                    quantity: 0,
                    unit: item.unit,
                    rate: item.rate,
                    amount: 0,
                  };
                }
              );

              const sectionTotal = tableData.reduce(
                (sum, item) => sum + item.amount,
                0
              );

              return (
                <>
                  <Table
                    dataSource={tableData}
                    columns={[
                      {
                        title: "Description",
                        dataIndex: "description",
                        key: "description",
                        width: "40%",
                      },
                      {
                        title: "Quantity",
                        dataIndex: "quantity",
                        key: "quantity",
                        width: "15%",
                        render: (value: number) => value.toFixed(3),
                      },
                      {
                        title: "Unit",
                        dataIndex: "unit",
                        key: "unit",
                        width: "10%",
                        render: (value: string) => formatUnit(value),
                      },
                      {
                        title: "Rate (£)",
                        dataIndex: "rate",
                        key: "rate",
                        width: "15%",
                        render: (value: number) => `£${value.toFixed(3)}`,
                      },
                      {
                        title: "Amount (£)",
                        dataIndex: "amount",
                        key: "amount",
                        width: "20%",
                        render: (value: number) => `£${value.toFixed(3)}`,
                      },
                    ]}
                    pagination={false}
                    size="small"
                  />
                  <div className="flex justify-end mt-2 mb-4">
                    <div className="bg-blue-50 p-2 rounded">
                      <Typography.Text strong className="text-blue-900">
                        Section Total: £{sectionTotal.toFixed(3)}
                      </Typography.Text>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Excavating & Filling Section */}
          <div className="mb-6">
            <Typography.Title level={4} className="mb-4 text-blue-900">
              {roadRates.excavatingFilling[0].category}
            </Typography.Title>

            {(() => {
              const rates = calculateAllRoadRates();
              const tableData = roadRates.excavatingFilling[0].items.map(
                (item, index) => {
                  let quantity = 0;
                  let amount = 0;

                  if (item.description === "Excavate to reduce levels") {
                    quantity = rates.totalCutVolume;
                    amount = quantity * item.rate;
                  } else if (
                    item.description ===
                    "Filling with site won subsoil to paths"
                  ) {
                    quantity = rates.totalFillVolumeFootpaths;
                    amount = quantity * item.rate;
                  } else if (
                    item.description === "Filling with recycled 6F2 to roads"
                  ) {
                    quantity = rates.totalFillVolumeCarriageways;
                    amount = quantity * item.rate;
                  } else if (
                    item.description === "Level and compact formation"
                  ) {
                    quantity = rates.totalArea;
                    amount = quantity * item.rate;
                  }

                  return {
                    key: index + 1,
                    description: item.description,
                    quantity: quantity,
                    unit: item.unit,
                    rate: item.rate,
                    amount: amount,
                  };
                }
              );

              const sectionTotal = tableData.reduce(
                (sum, item) => sum + item.amount,
                0
              );

              return (
                <>
                  <Table
                    dataSource={tableData}
                    columns={[
                      {
                        title: "Description",
                        dataIndex: "description",
                        key: "description",
                        width: "40%",
                      },
                      {
                        title: "Quantity",
                        dataIndex: "quantity",
                        key: "quantity",
                        width: "15%",
                        render: (value: number) => value.toFixed(3),
                      },
                      {
                        title: "Unit",
                        dataIndex: "unit",
                        key: "unit",
                        width: "10%",
                        render: (value: string) => formatUnit(value),
                      },
                      {
                        title: "Rate (£)",
                        dataIndex: "rate",
                        key: "rate",
                        width: "15%",
                        render: (value: number) => `£${value.toFixed(3)}`,
                      },
                      {
                        title: "Amount (£)",
                        dataIndex: "amount",
                        key: "amount",
                        width: "20%",
                        render: (value: number) => `£${value.toFixed(3)}`,
                      },
                    ]}
                    pagination={false}
                    size="small"
                  />
                  <div className="flex justify-end mt-2 mb-4">
                    <div className="bg-blue-50 p-2 rounded">
                      <Typography.Text strong className="text-blue-900">
                        Section Total: £{sectionTotal.toFixed(3)}
                      </Typography.Text>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Carriageway Sections */}
          {roadRates.carriageway.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-6">
              <Typography.Title level={4} className="mb-4 text-blue-900">
                {section.category}
              </Typography.Title>

              {(() => {
                const rates = calculateAllRoadRates();
                const tableData = section.items.map((item, index) => {
                  let quantity = 0;
                  let amount = 0;

                  if (section.category === "Macadam Access Road") {
                    if (item.description.includes("MOT Type 1")) {
                      quantity = rates.cappingRatioTarmac;
                      amount = quantity * item.rate;
                    } else if (
                      item.description.includes("AC 32mm DBM base course") ||
                      item.description.includes("AC 20mm DBM binder course") ||
                      item.description.includes("Sealing grit") ||
                      item.description.includes("Sweep tack") ||
                      item.description.includes("HRA 55/10F")
                    ) {
                      quantity = rates.totalTarmacRoad;
                      amount = quantity * item.rate;
                    }
                  } else if (section.category === "Block Paved Access Way") {
                    if (item.description.includes("MOT Type 1")) {
                      quantity = rates.cappingRatioBlockPaved;
                      amount = quantity * item.rate;
                    } else if (
                      item.description.includes("AC 32mm DBM base course")
                    ) {
                      quantity = rates.totalBlockPaved;
                      amount = quantity * item.rate;
                    } else if (
                      item.description.includes("Sealing grit") ||
                      item.description.includes("Sweep") ||
                      item.description.includes("block paviour")
                    ) {
                      quantity = rates.totalBlockPaved;
                      amount = quantity * item.rate;
                    } else if (item.description.includes("EO retrofit ramp")) {
                      quantity = rates.totalEoRamps;
                      amount = quantity * item.rate;
                    } else if (item.description.includes("EO form joint")) {
                      // Manual entry - set to 0
                      quantity = 0;
                      amount = 0;
                    }
                  }

                  return {
                    key: index + 1,
                    description: item.description,
                    quantity: quantity,
                    unit: item.unit,
                    rate: item.rate,
                    amount: amount,
                  };
                });

                const sectionTotal = tableData.reduce(
                  (sum, item) => sum + item.amount,
                  0
                );

                return (
                  <>
                    <Table
                      dataSource={tableData}
                      columns={[
                        {
                          title: "Description",
                          dataIndex: "description",
                          key: "description",
                          width: "40%",
                        },
                        {
                          title: "Quantity",
                          dataIndex: "quantity",
                          key: "quantity",
                          width: "15%",
                          render: (value: number) => value.toFixed(3),
                        },
                        {
                          title: "Unit",
                          dataIndex: "unit",
                          key: "unit",
                          width: "10%",
                          align: "center" as const,
                          render: (value: string) => formatUnit(value),
                        },
                        {
                          title: "Rate (£)",
                          dataIndex: "rate",
                          key: "rate",
                          width: "15%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                        {
                          title: "Amount (£)",
                          dataIndex: "amount",
                          key: "amount",
                          width: "20%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                    <div className="flex justify-end mt-2 mb-4">
                      <div className="bg-blue-50 p-2 rounded">
                        <Typography.Text strong className="text-blue-900">
                          Section Total: £{sectionTotal.toFixed(3)}
                        </Typography.Text>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ))}

          {/* Footpaths, Crossovers & Verges Sections */}
          {roadRates.footpathsCrossoversVerges.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-6">
              <Typography.Title level={4} className="mb-4 text-blue-900">
                {section.category}
              </Typography.Title>

              {(() => {
                const rates = calculateAllRoadRates();
                const tableData = section.items.map((item, index) => {
                  let quantity = 0;
                  let amount = 0;

                  if (section.category === "Tarmac Footpath") {
                    if (
                      item.description.includes("MOT Type 1") ||
                      item.description.includes("AC 20mm DBM binder course") ||
                      item.description.includes("Sweep tack") ||
                      item.description.includes("AC 6mm DBM surface course")
                    ) {
                      quantity = rates.totalTarmacPath;
                      amount = quantity * item.rate;
                    }
                  } else if (section.category === "Tarmac Crossover") {
                    if (
                      item.description.includes("MOT Type 1") ||
                      item.description.includes("AC 20mm DBM binder course") ||
                      item.description.includes("Sweep tack") ||
                      item.description.includes("AC 6mm DBM surface course")
                    ) {
                      quantity = rates.totalCrossover;
                      amount = quantity * item.rate;
                    }
                  } else if (section.category === "Verges") {
                    if (
                      item.description.includes("Spread and level") ||
                      item.description.includes("EO for bodpave")
                    ) {
                      quantity = rates.totalVerge;
                      amount = quantity * item.rate;
                    }
                  }

                  return {
                    key: index + 1,
                    description: item.description,
                    quantity: quantity,
                    unit: item.unit,
                    rate: item.rate,
                    amount: amount,
                  };
                });

                const sectionTotal = tableData.reduce(
                  (sum, item) => sum + item.amount,
                  0
                );

                return (
                  <>
                    <Table
                      dataSource={tableData}
                      columns={[
                        {
                          title: "Description",
                          dataIndex: "description",
                          key: "description",
                          width: "40%",
                        },
                        {
                          title: "Quantity",
                          dataIndex: "quantity",
                          key: "quantity",
                          width: "15%",
                          render: (value: number) => value.toFixed(3),
                        },
                        {
                          title: "Unit",
                          dataIndex: "unit",
                          key: "unit",
                          width: "10%",
                          align: "center" as const,
                          render: (value: string) => formatUnit(value),
                        },
                        {
                          title: "Rate (£)",
                          dataIndex: "rate",
                          key: "rate",
                          width: "15%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                        {
                          title: "Amount (£)",
                          dataIndex: "amount",
                          key: "amount",
                          width: "20%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                    <div className="flex justify-end mt-2 mb-4">
                      <div className="bg-blue-50 p-2 rounded">
                        <Typography.Text strong className="text-blue-900">
                          Section Total: £{sectionTotal.toFixed(3)}
                        </Typography.Text>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ))}

          {/* Kerbs & Edgings Section */}
          {roadRates.kerbsEdgings.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-6">
              <Typography.Title level={4} className="mb-4 text-blue-900">
                {section.category}
              </Typography.Title>

              {(() => {
                const rates = calculateAllRoadRates();
                const tableData = section.items.map((item, index) => {
                  let quantity = 0;
                  let amount = 0;

                  if (item.description.includes("Pcc Kerbs")) {
                    quantity = rates.totalKerb;
                    amount = quantity * item.rate;
                  } else if (item.description.includes("EO for xxmmØ dowel")) {
                    quantity = rates.totalKerb / 0.45;
                    amount = quantity * item.rate;
                  } else if (item.description.includes("Channel Kerbs")) {
                    quantity = rates.totalChannelKerb;
                    amount = quantity * item.rate;
                  } else if (
                    item.description.includes("Temporary macadam") ||
                    item.description.includes("Remove macadam")
                  ) {
                    // Manual entry - set to 0
                    quantity = 0;
                    amount = 0;
                  } else if (item.description.includes("150 x 50 pcc edging")) {
                    quantity = rates.totalEdgings;
                    amount = quantity * item.rate;
                  } else if (item.description.includes("Tactiles")) {
                    quantity = rates.totalTactiles;
                    amount = quantity * item.rate;
                  }

                  return {
                    key: index + 1,
                    description: item.description,
                    quantity: quantity,
                    unit: item.unit,
                    rate: item.rate,
                    amount: amount,
                  };
                });

                const sectionTotal = tableData.reduce(
                  (sum, item) => sum + item.amount,
                  0
                );

                return (
                  <>
                    <Table
                      dataSource={tableData}
                      columns={[
                        {
                          title: "Description",
                          dataIndex: "description",
                          key: "description",
                          width: "40%",
                        },
                        {
                          title: "Quantity",
                          dataIndex: "quantity",
                          key: "quantity",
                          width: "15%",
                          render: (value: number) => value.toFixed(3),
                        },
                        {
                          title: "Unit",
                          dataIndex: "unit",
                          key: "unit",
                          width: "10%",
                          align: "center" as const,
                          render: (value: string) => formatUnit(value),
                        },
                        {
                          title: "Rate (£)",
                          dataIndex: "rate",
                          key: "rate",
                          width: "15%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                        {
                          title: "Amount (£)",
                          dataIndex: "amount",
                          key: "amount",
                          width: "20%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                    <div className="flex justify-end mt-2 mb-4">
                      <div className="bg-blue-50 p-2 rounded">
                        <Typography.Text strong className="text-blue-900">
                          Section Total: £{sectionTotal.toFixed(3)}
                        </Typography.Text>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ))}

          {/* Adjust Ironwork Section */}
          {roadRates.adjustIronwork.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-6">
              <Typography.Title level={4} className="mb-4 text-blue-900">
                {section.category}
              </Typography.Title>

              {(() => {
                const rates = calculateAllRoadRates();
                const tableData = section.items.map((item, index) => {
                  let quantity = 0;
                  let amount = 0;

                  if (item.description.includes("Gully covers")) {
                    quantity = rates.totalGullies;
                    amount = quantity * item.rate;
                  } else if (
                    item.description.includes("Manholes") ||
                    item.description.includes("Stop Tap") ||
                    item.description.includes("BT / Virgin")
                  ) {
                    // Manual entry - set to 0
                    quantity = 0;
                    amount = 0;
                  }

                  return {
                    key: index + 1,
                    description: item.description,
                    quantity: quantity,
                    unit: item.unit,
                    rate: item.rate,
                    amount: amount,
                  };
                });

                const sectionTotal = tableData.reduce(
                  (sum, item) => sum + item.amount,
                  0
                );

                return (
                  <>
                    <Table
                      dataSource={tableData}
                      columns={[
                        {
                          title: "Description",
                          dataIndex: "description",
                          key: "description",
                          width: "40%",
                        },
                        {
                          title: "Quantity",
                          dataIndex: "quantity",
                          key: "quantity",
                          width: "15%",
                          render: (value: number) => value.toFixed(3),
                        },
                        {
                          title: "Unit",
                          dataIndex: "unit",
                          key: "unit",
                          width: "10%",
                          align: "center" as const,
                          render: (value: string) => formatUnit(value),
                        },
                        {
                          title: "Rate (£)",
                          dataIndex: "rate",
                          key: "rate",
                          width: "15%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                        {
                          title: "Amount (£)",
                          dataIndex: "amount",
                          key: "amount",
                          width: "20%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                    <div className="flex justify-end mt-2 mb-4">
                      <div className="bg-blue-50 p-2 rounded">
                        <Typography.Text strong className="text-blue-900">
                          Section Total: £{sectionTotal.toFixed(3)}
                        </Typography.Text>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ))}

          {/* Remaining sections with manual entries */}
          {[
            ...roadRates.whiteLining,
            ...roadRates.streetNameplates,
            ...roadRates.streetFurniture,
            ...roadRates.remedials,
          ].map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-6">
              <Typography.Title level={4} className="mb-4 text-blue-900">
                {section.category}
              </Typography.Title>

              {(() => {
                const tableData = section.items.map((item, index) => ({
                  key: index + 1,
                  description: item.description,
                  quantity: 0, // Manual entry
                  unit: item.unit,
                  rate: item.rate,
                  amount: 0, // Manual entry
                }));

                const sectionTotal = tableData.reduce(
                  (sum, item) => sum + item.amount,
                  0
                );

                return (
                  <>
                    <Table
                      dataSource={tableData}
                      columns={[
                        {
                          title: "Description",
                          dataIndex: "description",
                          key: "description",
                          width: "40%",
                        },
                        {
                          title: "Quantity",
                          dataIndex: "quantity",
                          key: "quantity",
                          width: "15%",
                          render: (value: number) => value.toFixed(3),
                        },
                        {
                          title: "Unit",
                          dataIndex: "unit",
                          key: "unit",
                          width: "10%",
                          align: "center" as const,
                          render: (value: string) => formatUnit(value),
                        },
                        {
                          title: "Rate (£)",
                          dataIndex: "rate",
                          key: "rate",
                          width: "15%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                        {
                          title: "Amount (£)",
                          dataIndex: "amount",
                          key: "amount",
                          width: "20%",
                          render: (value: number) => `£${value.toFixed(3)}`,
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                    <div className="flex justify-end mt-2 mb-4">
                      <div className="bg-blue-50 p-2 rounded">
                        <Typography.Text strong className="text-blue-900">
                          Section Total: £{sectionTotal.toFixed(3)}
                        </Typography.Text>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ))}

          {/* Grand Total */}
          {(() => {
            const rates = calculateAllRoadRates();

            // Calculate totals for each section
            const siteClearanceTotal = (() => {
              const tableData = roadRates.siteClearanceEarthworks[0].items.map(
                (item) => {
                  if (item.description === "Site strip to remove vegetation") {
                    return rates.siteStripAmount;
                  } else if (
                    item.description ===
                    "Excavate to reduce levels to take up 296mm top soil, deposit onsite"
                  ) {
                    return rates.excavateAmount;
                  }
                  return 0;
                }
              );
              return tableData.reduce((sum, amount) => sum + amount, 0);
            })();

            const excavatingFillingTotal = (() => {
              const tableData = roadRates.excavatingFilling[0].items.map(
                (item) => {
                  let amount = 0;
                  if (item.description === "Excavate to reduce levels") {
                    amount = rates.totalCutVolume * item.rate;
                  } else if (
                    item.description ===
                    "Filling with site won subsoil to paths"
                  ) {
                    amount = rates.totalFillVolumeFootpaths * item.rate;
                  } else if (
                    item.description === "Filling with recycled 6F2 to roads"
                  ) {
                    amount = rates.totalFillVolumeCarriageways * item.rate;
                  } else if (
                    item.description === "Level and compact formation"
                  ) {
                    amount = rates.totalArea * item.rate;
                  }
                  return amount;
                }
              );
              return tableData.reduce((sum, amount) => sum + amount, 0);
            })();

            const carriagewayTotal = roadRates.carriageway.reduce(
              (sectionSum, section) => {
                const tableData = section.items.map((item) => {
                  let amount = 0;
                  if (section.category === "Macadam Access Road") {
                    if (item.description.includes("MOT Type 1")) {
                      amount = rates.cappingRatioTarmac * item.rate;
                    } else if (
                      item.description.includes("AC 32mm DBM base course") ||
                      item.description.includes("AC 20mm DBM binder course") ||
                      item.description.includes("Sealing grit") ||
                      item.description.includes("Sweep tack") ||
                      item.description.includes("HRA 55/10F")
                    ) {
                      amount = rates.totalTarmacRoad * item.rate;
                    }
                  } else if (section.category === "Block Paved Access Way") {
                    if (item.description.includes("MOT Type 1")) {
                      amount = rates.cappingRatioBlockPaved * item.rate;
                    } else if (
                      item.description.includes("AC 32mm DBM base course")
                    ) {
                      amount = rates.totalBlockPaved * item.rate;
                    } else if (
                      item.description.includes("Sealing grit") ||
                      item.description.includes("Sweep") ||
                      item.description.includes("block paviour")
                    ) {
                      amount = rates.totalBlockPaved * item.rate;
                    } else if (item.description.includes("EO retrofit ramp")) {
                      amount = rates.totalEoRamps * item.rate;
                    }
                  }
                  return amount;
                });
                return (
                  sectionSum +
                  tableData.reduce((sum, amount) => sum + amount, 0)
                );
              },
              0
            );

            const footpathsTotal = roadRates.footpathsCrossoversVerges.reduce(
              (sectionSum, section) => {
                const tableData = section.items.map((item) => {
                  let amount = 0;
                  if (section.category === "Tarmac Footpath") {
                    if (
                      item.description.includes("MOT Type 1") ||
                      item.description.includes("AC 20mm DBM binder course") ||
                      item.description.includes("Sweep tack") ||
                      item.description.includes("AC 6mm DBM surface course")
                    ) {
                      amount = rates.totalTarmacPath * item.rate;
                    }
                  } else if (section.category === "Tarmac Crossover") {
                    if (
                      item.description.includes("MOT Type 1") ||
                      item.description.includes("AC 20mm DBM binder course") ||
                      item.description.includes("Sweep tack") ||
                      item.description.includes("AC 6mm DBM surface course")
                    ) {
                      amount = rates.totalCrossover * item.rate;
                    }
                  } else if (section.category === "Verges") {
                    if (
                      item.description.includes("Spread and level") ||
                      item.description.includes("EO for bodpave")
                    ) {
                      amount = rates.totalVerge * item.rate;
                    }
                  }
                  return amount;
                });
                return (
                  sectionSum +
                  tableData.reduce((sum, amount) => sum + amount, 0)
                );
              },
              0
            );

            const kerbsEdgingsTotal = roadRates.kerbsEdgings.reduce(
              (sectionSum, section) => {
                const tableData = section.items.map((item) => {
                  let amount = 0;
                  if (item.description.includes("Pcc Kerbs")) {
                    amount = rates.totalKerb * item.rate;
                  } else if (item.description.includes("EO for xxmmØ dowel")) {
                    amount = (rates.totalKerb / 0.45) * item.rate;
                  } else if (item.description.includes("Channel Kerbs")) {
                    amount = rates.totalChannelKerb * item.rate;
                  } else if (item.description.includes("150 x 50 pcc edging")) {
                    amount = rates.totalEdgings * item.rate;
                  } else if (item.description.includes("Tactiles")) {
                    amount = rates.totalTactiles * item.rate;
                  }
                  return amount;
                });
                return (
                  sectionSum +
                  tableData.reduce((sum, amount) => sum + amount, 0)
                );
              },
              0
            );

            const ironworkTotal = roadRates.adjustIronwork.reduce(
              (sectionSum, section) => {
                const tableData = section.items.map((item) => {
                  let amount = 0;
                  if (item.description.includes("Gully covers")) {
                    amount = rates.totalGullies * item.rate;
                  }
                  return amount;
                });
                return (
                  sectionSum +
                  tableData.reduce((sum, amount) => sum + amount, 0)
                );
              },
              0
            );

            // Manual entry sections have 0 totals
            const manualEntriesTotal = 0;

            const grandTotal =
              siteClearanceTotal +
              excavatingFillingTotal +
              carriagewayTotal +
              footpathsTotal +
              kerbsEdgingsTotal +
              ironworkTotal +
              manualEntriesTotal;

            return (
              <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
                <div className="flex justify-between items-center">
                  <Typography.Title level={3} className="text-blue-900 mb-0">
                    Grand Total
                  </Typography.Title>
                  <Typography.Title level={2} className="text-blue-900 mb-0">
                    £{grandTotal.toFixed(3)}
                  </Typography.Title>
                </div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Site Clearance & Earthworks:
                    </span>
                    <span className="font-semibold">
                      £{siteClearanceTotal.toFixed(3)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Excavating & Filling:</span>
                    <span className="font-semibold">
                      £{excavatingFillingTotal.toFixed(3)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Carriageway:</span>
                    <span className="font-semibold">
                      £{carriagewayTotal.toFixed(3)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Footpaths & Verges:</span>
                    <span className="font-semibold">
                      £{footpathsTotal.toFixed(3)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Kerbs & Edgings:</span>
                    <span className="font-semibold">
                      £{kerbsEdgingsTotal.toFixed(3)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ironwork:</span>
                    <span className="font-semibold">
                      £{ironworkTotal.toFixed(3)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </Modal>

      {/* Carriageway Cut & Fill — Configure Modal */}
      <ColumnConfigModal
        title="Carriageway Cut & Fill — Configure"
        open={cutFillConfigOpen}
        onClose={() => {
          if (cutFillConfigSnapshotRef.current) {
            updateField("roads", { ...roadsData, cutFillSections: { ...roadsData.cutFillSections, config: cutFillConfigSnapshotRef.current } });
          }
          setCutFillConfigOpen(false);
        }}
        onApply={() => setCutFillConfigOpen(false)}
        width={400}
        isViewMode={state.isViewMode}
      >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <ConfigField label="Strip Value (m)">
          <NumberInput
            value={roadsData.cutFillSections.config.stripValueDefault ?? 0.3}
            onChange={(value) =>
              updateField("roads", {
                ...roadsData,
                cutFillSections: { ...roadsData.cutFillSections, config: { ...roadsData.cutFillSections.config, stripValueDefault: value } },
              })
            }
            decimalPlaces={3}
            style={{ width: "100%", textAlign: "right" }}
            placeholder="0.300"
          />
        </ConfigField>
        <ConfigField label="Tarmac Construction Type">
          <Select
            value={roadsData.cutFillSections.config.tarmacConstructionTypeId}
            onChange={(value) =>
              updateField("roads", {
                ...roadsData,
                cutFillSections: { ...roadsData.cutFillSections, config: { ...roadsData.cutFillSections.config, tarmacConstructionTypeId: value } },
              })
            }
            placeholder="Select type"
            size="small"
            style={{ width: "100%" }}
            allowClear
          >
            {(roadsData.roadTypes.table || [])
              .filter((rt) => rt.surface !== "Block Paved Road")
              .map((rt) => (
                <Select.Option key={rt.id} value={rt.id}>
                  {rt.name}
                </Select.Option>
              ))}
          </Select>
        </ConfigField>
        <ConfigField label="Block Paved Construction Type">
          <Select
            value={roadsData.cutFillSections.config.blockPavedConstructionTypeId}
            onChange={(value) =>
              updateField("roads", {
                ...roadsData,
                cutFillSections: { ...roadsData.cutFillSections, config: { ...roadsData.cutFillSections.config, blockPavedConstructionTypeId: value } },
              })
            }
            placeholder="Select type"
            size="small"
            style={{ width: "100%" }}
            allowClear
          >
            {(roadsData.roadTypes.table || [])
              .filter((rt) => rt.surface === "Block Paved Road")
              .map((rt) => (
                <Select.Option key={rt.id} value={rt.id}>
                  {rt.name}
                </Select.Option>
              ))}
          </Select>
        </ConfigField>
      </div>
      </ColumnConfigModal>

      {/* Kerbs & Edgings — Configure Modal */}
      <ColumnConfigModal
        title="Kerbs & Edgings — Configure"
        open={kerbsEdgingsConfigOpen}
        onClose={() => {
          if (kerbsEdgingsConfigSnapshotRef.current) {
            updateField("roads", { ...roadsData, carriagewayFootways: { ...roadsData.carriagewayFootways, config: kerbsEdgingsConfigSnapshotRef.current } });
          }
          setKerbsEdgingsConfigOpen(false);
        }}
        onApply={() => setKerbsEdgingsConfigOpen(false)}
        width={400}
        isViewMode={state.isViewMode}
      >
        <ConfigField label="Kerb Race Width (m)">
          <Select
            value={roadsData.carriagewayFootways.config.kerbRaceWidth ?? 0.5}
            onChange={(value) =>
              updateField("roads", {
                ...roadsData,
                carriagewayFootways: { ...roadsData.carriagewayFootways, config: { ...roadsData.carriagewayFootways.config, kerbRaceWidth: value } },
              })
            }
            style={{ width: "100%" }}
          >
            {KERB_RACE_WIDTH_OPTIONS.map((width) => (
              <Select.Option key={width} value={width}>
                {width.toFixed(3)}
              </Select.Option>
            ))}
          </Select>
        </ConfigField>
      </ColumnConfigModal>

      {/* Road Types — Construction Configuration Modal — unlike every other
          Configure modal on this page (ColumnConfigModal-based, gated via its
          own isViewMode prop), this one is a plain Modal with a hand-rolled
          <table> of Input/Select cells that never checked isViewMode at all,
          so it stayed fully editable even when the page itself was read-only.
          Reuses ColumnConfigModal's own .view-mode-modal-content CSS class
          (index.css — disables plain input/select and antd
          Input/Select/InputNumber generically) instead of gating every cell
          individually. */}
      <Modal
        title={state.isViewMode ? "Road Types — Construction Configuration (View Only)" : "Road Types — Construction Configuration"}
        open={roadTypesConfigOpen}
        onCancel={() => {
          if (roadTypesConfigSnapshotRef.current) {
            updateField("roads", { ...roadsData, roadTypes: { ...roadsData.roadTypes, table: roadTypesConfigSnapshotRef.current } });
          }
          setRoadTypesConfigOpen(false);
        }}
        onOk={() => setRoadTypesConfigOpen(false)}
        okText={state.isViewMode ? "Close" : "Done"}
        cancelButtonProps={{ style: { display: "none" } }}
        width="min(1500px, calc(100vw - 48px))"
        styles={{ body: { padding: "16px 0" } }}
      >
        <div className={state.isViewMode ? "view-mode-modal-content" : ""}>
          {renderRoadTypesTable()}
        </div>
      </Modal>

      {/* Footpaths — Construction Configuration Modal */}
      <FootpathsConfigModal
        open={footpathsConfigOpen}
        onClose={() => setFootpathsConfigOpen(false)}
        onCancel={() => {
          if (footpathsConfigSnapshotRef.current) {
            updateField("roads", { ...roadsData, footpaths: { ...roadsData.footpaths, config: footpathsConfigSnapshotRef.current } });
          }
          setFootpathsConfigOpen(false);
        }}
        footpathsConfig={resolvedFootpathsConfig}
        onUpdate={(config: FootpathsConstructionConfig) =>
          updateField("roads", { ...roadsData, footpaths: { ...roadsData.footpaths, config } })
        }
        isViewMode={state.isViewMode}
      />

      {/* Cut & Fill / Footpaths column calculation info modal */}
      {colCalcInfo && (
        <Modal
          title={`${colCalcInfo.title} — Calculation`}
          open={true}
          onCancel={() => setColCalcInfo(null)}
          footer={[
            <Button key="close" onClick={() => setColCalcInfo(null)}>
              Close
            </Button>,
          ]}
          width={500}
        >
          <div style={{ fontSize: 13, color: "#374151" }}>
            <div
              style={{
                padding: "8px 12px",
                background: "#f0f4ff",
                borderRadius: 4,
                fontFamily: "ui-monospace, monospace",
                fontSize: 12,
              }}
            >
              {colCalcInfo.formula}
            </div>
          </div>
        </Modal>
      )}

      {/* Excel Import Modal */}
      <ExcelImportModal
        onImport={handleUnifiedImport}
        onDownloadTemplate={handleDownloadTemplate}
        importLoading={importLoading}
        importModalVisible={importModalVisible}
        setImportModalVisible={setImportModalVisible}
      />

      <style>{`
        .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
        }

        .roads-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .roads-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .roads-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .roads-card .ant-card-body {
          overflow: visible !important;
        }

        .roads-sticky-header {
          box-shadow: none;
          z-index: 98 !important;
        }

        .roads-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        .roads-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }
      `}</style>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={pendingDeleteCount > 1 ? `Are you sure you want to delete ${pendingDeleteCount} records?` : undefined}
      />
    </MainLayout>
  );
};

export default RoadsTakeoff;
